// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.

// frontend/src/components/MissionExecutionCenter.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./MissionExecutionCenter.css";
import TaskExecutionModal from "./mission-execution/TaskExecutionModal";
import MissionExecutionIntelligencePanel from "./mission-execution/MissionExecutionIntelligencePanel";
import EstimatedEffortBadge from "./work-optimization/EstimatedEffortBadge";
import OptimizationInsightPanel from "./work-optimization/OptimizationInsightPanel";
import OptimizationSummaryStrip from "./work-optimization/OptimizationSummaryStrip";
import ExecutionPlanningSummaryStrip from "./execution-plans/ExecutionPlanningSummaryStrip";
import LearningSummaryStrip from "./learning/LearningSummaryStrip";
import { useAdvisor } from "../AdvisorContext";

const REQUEST_TIMEOUT_MS = 30000;
const LEARNING_REFRESH_MIN_VISIBLE_MS = 750;
const LEARNING_REFRESH_SUCCESS_VISIBLE_MS = 2000;

const QUEUE_GROUP_LABELS = {
  high_priority: "High Priority",
  ready_to_work: "Ready Now",
  waiting_validation: "Waiting Validation",
  blocked: "Blocked",
  awaiting_approval: "Awaiting Approval",
  ready_for_closure: "Ready For Closure",
  overdue: "Overdue",
  recently_completed: "Completed",
};


const FILTERS = [
  { key: "ai_priority", label: "AI Priority" },
  { key: "quick_wins", label: "Quick Wins" },
  { key: "fast_follows", label: "Fast Follows" },
  { key: "high_impact", label: "High Impact" },
  { key: "batch_opportunities", label: "Batches" },
  { key: "validation_ready", label: "Validation Ready" },
  { key: "my_work", label: "My Work" },
  { key: "ready_to_work", label: "Ready Now" },
  { key: "blocked", label: "Blocked" },
  { key: "overdue", label: "Overdue" },
  { key: "recently_completed", label: "Completed" },
];

const CONTEXT_TABS = [
  { key: "evidence", label: "Evidence" },
  { key: "workflow", label: "Workflow" },
  { key: "validation", label: "Validation" },
  { key: "sop", label: "SOP" },
  { key: "history", label: "History" },
];

const PROGRESS_STEPS = [
  "Recommendation",
  "Accepted",
  "Converted",
  "Remediation",
  "Workflow",
  "Validation",
  "Closure",
];

function clean(value) {
  const text = String(value ?? "").trim();
  return text || "";
}

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10) || "—";
  return date.toLocaleDateString();
}

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
}

function formatQueueGroup(value) {
  const key = clean(value).toLowerCase();
  return QUEUE_GROUP_LABELS[key] || clean(value).replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase()) || "Queue";
}

function normalizeItems(value) {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.queue)) return value.queue;
  if (Array.isArray(value?.timeline)) return value.timeline;
  return [];
}

function titleCase(value) {
  const cleaned = clean(value);
  if (!cleaned) return "—";
  return cleaned
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function humanizeDisplayValue(value, fallback = "—") {
  const raw = clean(value);
  if (!raw) return fallback;

  const normalized = raw.replace(/_/g, " ").replace(/\s+/g, " ").trim();
  const lower = normalized.toLowerCase();

  const exactLabels = {
    soc2: "SOC2",
    "soc 2": "SOC2",
    nist: "NIST",
    iso: "ISO",
    hipaa: "HIPAA",
    ccpa: "CCPA",
    aml: "AML",
    kyc: "KYC",
    fda: "FDA",
    osha: "OSHA",
    sop: "SOP",
    aws: "AWS",
    s3: "S3",
    iam: "IAM",
    waf: "WAF",
    api: "API",
    cloudflare: "Cloudflare",
    okta: "Okta",
    "google workspace": "Google Workspace",
    "risk queue": "Risk Queue",
    "risk queue default": "Risk Queue Default",
    "validation rerun": "Validation Rerun",
    "validation evidence link": "Validation Evidence",
    "remediation task evidence": "Remediation Evidence",
    "archive record": "Archive Record",
    "task scoped": "Task Scoped",
    "not linked": "Not Linked",
    "not loaded": "Not Loaded",
    "in progress": "In Progress",
    "ready for validation": "Ready for Validation",
    "ready to work": "Ready to Work",
    "awaiting validation": "Awaiting Validation",
    "awaiting approval": "Awaiting Approval",
  };

  if (exactLabels[lower]) return exactLabels[lower];

  return normalized
    .split(" ")
    .map((word) => {
      const key = word.toLowerCase();
      if (exactLabels[key]) return exactLabels[key];
      if (/^cc\d+(?:\.\d+)?$/i.test(word)) return word.toUpperCase();
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
}

function formatPercentish(value) {
  if (value === null || value === undefined || value === "") return "—";
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return String(value);
  return `${Math.round(parsed)}%`;
}

function firstArray(...values) {
  for (const value of values) {
    if (Array.isArray(value) && value.length) return value;
  }
  return [];
}

function valueOrDash(value) {
  return clean(value) || "—";
}

function normalizeTaskId(value) {
  const text = clean(value);
  if (!text) return "";
  return text.startsWith("task:") ? text.slice(5) : text;
}

function taskIdCandidates(item = {}) {
  return [
    item?.work_item_id,
    item?.id,
    item?.task_id,
    item?.taskId,
    item?.remediation_task_id,
    item?.remediationTaskId,
    item?.raw_task?.id,
    item?.raw_task?.task_id,
  ]
    .map(normalizeTaskId)
    .filter(Boolean);
}

function optimizationItems(value) {
  return Array.isArray(value) ? value : [];
}

function optimizationCategoryForItem(item, lookup) {
  const candidates = taskIdCandidates(item);
  for (const candidate of candidates) {
    const match = lookup.get(candidate);
    if (match) return match;
  }
  return null;
}

function summarizeWorkflowItem(value, fallback = "—") {
  if (value === null || value === undefined || value === "") return fallback;
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return titleCase(value);
  }

  if (typeof value === "object") {
    return (
      clean(value.title) ||
      clean(value.name) ||
      clean(value.label) ||
      clean(value.message) ||
      clean(value.reason) ||
      clean(value.status) ||
      clean(value.state) ||
      clean(value.id) ||
      fallback
    );
  }

  return fallback;
}

function workflowCount(value) {
  if (Array.isArray(value)) return value.length;
  if (value && typeof value === "object") {
    if (typeof value.count === "number") return value.count;
    if (typeof value.total === "number") return value.total;
    if (Array.isArray(value.items)) return value.items.length;
    return Object.keys(value).length;
  }
  return 0;
}

function timelineEventType(event) {
  const raw = clean(event?.event_type || event?.type || event?.category).toLowerCase();
  const title = clean(event?.title).toLowerCase();
  const message = clean(event?.message).toLowerCase();
  const combined = `${raw} ${title} ${message}`;

  if (combined.includes("validation")) return { label: "Validation", tone: "warn", className: "validation" };
  if (combined.includes("workflow")) return { label: "Workflow", tone: "ok", className: "workflow" };
  if (combined.includes("evidence")) return { label: "Evidence", tone: "neutral", className: "evidence" };
  if (combined.includes("operator") || combined.includes("note")) return { label: "Operator", tone: "neutral", className: "operator" };
  if (combined.includes("sync") || combined.includes("system")) return { label: "System", tone: "neutral", className: "system" };
  if (combined.includes("block")) return { label: "Blocked", tone: "danger", className: "blocked" };
  if (combined.includes("remediation") || combined.includes("task")) return { label: "Work", tone: "neutral", className: "work" };
  return { label: "Event", tone: "neutral", className: "event" };
}

function timelineActor(event) {
  return (
    clean(event?.actor_name) ||
    clean(event?.actor_email) ||
    clean(event?.actor_id) ||
    clean(event?.user_name) ||
    clean(event?.metadata?.actor_name) ||
    clean(event?.metadata?.actor_email) ||
    clean(event?.metadata?.user_name) ||
    "OASYS"
  );
}

function timelineTitle(event) {
  return clean(event?.title) || titleCase(event?.event_type || event?.type || "event");
}

function timelineMessage(event) {
  return (
    clean(event?.message) ||
    clean(event?.metadata?.message) ||
    clean(event?.metadata?.note) ||
    clean(event?.description) ||
    "No event message recorded."
  );
}


function MetricCard({ label, value, footnote, tone = "default" }) {
  return (
    <div className={`mec-summary-card mec-summary-card-${tone}`}>
      <span>{label}</span>
      <strong>{value ?? "—"}</strong>
      {footnote ? <small>{footnote}</small> : null}
    </div>
  );
}

function EmptyState({ title, message }) {
  return (
    <div className="mec-empty-state">
      <strong>{title}</strong>
      <span>{message}</span>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="mec-detail-row">
      <span>{label}</span>
      <strong>{valueOrDash(value)}</strong>
    </div>
  );
}

function StatusPill({ children, tone = "neutral" }) {
  return <span className={`mec-pill mec-pill-${tone}`}>{children || "—"}</span>;
}


function decisionPriorityTone(decision) {
  const priority = clean(decision?.priority).toLowerCase();
  const score = Number(decision?.total_score);
  if (priority === "critical" || score >= 85) return "danger";
  if (priority === "high" || score >= 70) return "warn";
  if (clean(decision?.confidence_label).toLowerCase() === "high") return "ok";
  return "neutral";
}

function decisionStatusText(decision) {
  const confidence = decision?.confidence_score ?? decision?.confidence?.confidence_score;
  const parsed = Number(confidence);
  const confidenceText = Number.isFinite(parsed) ? `Confidence ${Math.round(parsed)}%` : "Confidence —";
  return `${titleCase(decision?.decision_type || "Decision")} · ${confidenceText}`;
}

function decisionAffectedTaskId(decision) {
  return clean(decision?.affected_task || decision?.affectedTask || decision?.task_id || decision?.taskId);
}

function remediationTaskIdForValidation(task, workItemId = "") {
  const candidates = [
    task?.task_id,
    task?.remediation_task_id,
    task?.raw_task?.id,
    task?.raw_task?.task_id,
    task?.id,
    workItemId,
  ];

  for (const candidate of candidates) {
    let value = clean(candidate);
    if (!value) continue;
    if (value.startsWith("task:")) value = value.slice(5);

    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)) {
      return value;
    }
  }

  return "";
}

function decisionNormalizedId(decision) {
  return clean(decision?.decision_id || decision?.id);
}

function sameDecision(left, right) {
  const leftId = decisionNormalizedId(left);
  const rightId = decisionNormalizedId(right);
  return Boolean(leftId && rightId && leftId === rightId);
}

function uniqueTextItems(values, limit = 5) {
  const seen = new Set();
  const output = [];

  (values || []).forEach((value) => {
    const text = clean(value);
    const key = text.toLowerCase();
    if (!text || seen.has(key)) return;
    seen.add(key);
    output.push(operatorFriendlyWorkflowText(text));
  });

  return output.slice(0, limit);
}

function decisionActionSteps(decision) {
  const directSteps = Array.isArray(decision?.action_steps) ? decision.action_steps : [];
  const detailSteps = Array.isArray(decision?.reasoning?.action_steps) ? decision.reasoning.action_steps : [];
  const expectedImpact = Array.isArray(decision?.reasoning?.expected_impact) ? decision.reasoning.expected_impact : [];

  const steps = uniqueTextItems([...directSteps, ...detailSteps], 4);
  if (steps.length) return steps;

  const recommendation = clean(decision?.recommendation).toLowerCase();
  if (recommendation.includes("workflow")) {
    return [
      "Open the affected task and review the workflow tab.",
      "Confirm whether the workflow is stuck, queued, or failed before changing task state.",
      "Document the operator decision before retrying or continuing manually.",
    ];
  }

  if (recommendation.includes("validation")) {
    return [
      "Review the validation blocker and current evidence state.",
      "Refresh connector evidence or normalization if the source data may be stale.",
      "Rerun validation only after the blocker is addressed.",
    ];
  }

  if (expectedImpact.length) return uniqueTextItems(expectedImpact, 3);

  return [
    "Review the affected task context.",
    "Confirm evidence, workflow, and validation state.",
    "Take the next operator-controlled action.",
  ];
}

function compactSignalText(value, fallback = "") {
  const text = clean(value);
  if (!text) return fallback;

  const normalized = text.toLowerCase();
  if (["unknown", "none", "null", "undefined", "not_loaded", "not linked", "not_linked"].includes(normalized)) {
    return fallback || "No specific context is linked yet.";
  }

  return text;
}

function operationalSignalLabel(label, value = "") {
  const raw = clean(label).toLowerCase();
  const text = clean(value).toLowerCase();

  if (["validation", "workflow", "evidence", "risk"].includes(raw)) return titleCase(raw);

  if (raw && !["signal", "decision signal", "decision_signal", "context", "item"].includes(raw)) {
    return titleCase(raw);
  }

  if (text.includes("validation") || text.includes("failing") || text.includes("closure")) return "Validation";
  if (text.includes("workflow") || text.includes("queued") || text.includes("transition")) return "Workflow";
  if (text.includes("evidence") || text.includes("source-system") || text.includes("source system")) return "Evidence";
  if (text.includes("risk") || text.includes("impact") || text.includes("framework")) return "Risk";

  return "Signal";
}

function signalHelpText(label, value) {
  const normalizedLabel = clean(label).toLowerCase();
  const normalizedValue = clean(value).toLowerCase();

  if (normalizedLabel === "validation") {
    if (normalizedValue.includes("failed")) return "needs review";
    if (normalizedValue.includes("passed")) return "supports closure";
    if (normalizedValue.includes("stale")) return "refresh needed";
    return "validation context";
  }

  if (normalizedLabel === "workflow") {
    if (normalizedValue.includes("queued")) return "workflow waiting";
    if (normalizedValue.includes("failed") || normalizedValue.includes("transition")) return "recovery signal";
    if (normalizedValue.includes("not linked")) return "manual path";
    return "workflow context";
  }

  if (normalizedLabel === "evidence") {
    if (normalizedValue.includes("missing") || normalizedValue.includes("no specific")) return "evidence gap";
    if (normalizedValue.includes("stale")) return "refresh needed";
    if (normalizedValue.includes("linked")) return "evidence context";
    return "evidence context";
  }

  if (normalizedLabel === "risk") {
    return "operational impact";
  }

  return "supporting context";
}

function displaySignalToken(value) {
  const text = clean(value).replace(/_/g, " ");
  if (!text) return "";

  const upper = text.toUpperCase();
  if (["SOC2", "SOC 2", "NIST", "ISO", "HIPAA", "CCPA", "AML", "KYC", "FDA", "OSHA"].includes(upper)) {
    return upper === "SOC 2" ? "SOC2" : upper;
  }

  return titleCase(text);
}

function truncateAtWord(value, maxLength = 58) {
  const text = clean(value).replace(/\s+/g, " ");
  if (text.length <= maxLength) return text;

  const truncated = text.slice(0, maxLength).replace(/[\s,.;:·-]+\S*$/, "");
  return `${truncated || text.slice(0, maxLength).trim()}…`;
}

function transitionSummaryFromText(value) {
  const text = clean(value);
  if (!text) return "";

  const transitionMatch = text.match(/(?:transition|step transition)[:\s]+['"]?([^'"]+?)['"]?\s*->\s*['"]?([^'".]+)['"]?/i);
  if (!transitionMatch) return "";

  const fromState = clean(transitionMatch[1]).replace(/[.]+$/, "");
  const toState = clean(transitionMatch[2]).replace(/[.]+$/, "");

  if (!fromState || !toState) return "";

  return `Workflow recovery appears available because the workflow transition from ${fromState} to ${toState} failed.`;
}

function workflowStateLabel(value = "") {
  const normalized = clean(value)
    .replace(/["']/g, "")
    .trim()
    .toLowerCase();

  if (!normalized) return "the prior state";
  if (normalized === "queued") return "Queued";
  if (normalized === "awaiting_validation") return "Awaiting Validation";
  if (normalized === "awaiting_approval") return "Awaiting Approval";
  if (normalized === "ready_for_validation") return "Ready for Validation";
  if (normalized === "in_progress") return "In Progress";
  return titleCase(normalized.replace(/_/g, " "));
}

function operatorFriendlyWorkflowText(value = "") {
  const text = clean(value);
  if (!text) return "";

  const transitionMatch = text.match(
    /(?:illegal\s+)?workflow\s+step\s+transition[:\s]+['"]?([^'"]+?)['"]?\s*->\s*['"]?([^'".]+)['"]?/i,
  );

  if (transitionMatch) {
    const fromState = workflowStateLabel(transitionMatch[1]);
    const toState = workflowStateLabel(transitionMatch[2]);
    return `The workflow could not move from ${fromState} to ${toState}. Review the current workflow state before retrying.`;
  }

  return text
    .replace(/awaiting_validation/g, "Awaiting Validation")
    .replace(/awaiting_approval/g, "Awaiting Approval")
    .replace(/ready_for_validation/g, "Ready for Validation")
    .replace(/in_progress/g, "In Progress");
}

function conciseRiskFromParts(framework, connector, validationStatus, taskStatus) {
  const parts = [];

  const frameworkText = displaySignalToken(framework);
  const connectorText = displaySignalToken(connector);
  const validationText = clean(validationStatus).toLowerCase();
  const taskText = clean(taskStatus).toLowerCase();

  if (frameworkText) parts.push(frameworkText);
  if (connectorText && connectorText.toLowerCase() !== "unknown") parts.push(connectorText);

  if (validationText && validationText !== "unknown") {
    parts.push(`validation ${validationText}`);
  } else if (taskText && taskText !== "unknown") {
    parts.push(`task ${taskText}`);
  }

  return parts.length ? truncateAtWord(parts.join(" · "), 58) : "";
}

function conciseRiskSignal(value, decision) {
  const text = clean(value);
  if (!text) return "";

  const framework = clean(decision?.affected_framework || decision?.framework_impact);
  const connector = clean(decision?.affected_connector);
  const validationStatus = clean(decision?.validation_status);
  const taskStatus = clean(decision?.task_status);

  const fromStructuredFields = conciseRiskFromParts(framework, connector, validationStatus, taskStatus);
  if (fromStructuredFields) return fromStructuredFields;

  const normalized = text
    .replace(/[•|]/g, "·")
    .replace(/\s*·\s*/g, " · ")
    .replace(/\s+/g, " ")
    .trim();

  const lowered = normalized.toLowerCase();
  if (lowered.includes("reduces operator decision latency")) {
    return "Operator decision latency risk.";
  }

  const withoutTaskTail = normalized.replace(/\s+·\s+task\b.*$/i, "");
  const tokens = withoutTaskTail
    .split("·")
    .map((token) => clean(token))
    .filter(Boolean);

  if (tokens.length >= 2) {
    const formatted = tokens.slice(0, 3).map((token, index) => {
      if (index <= 1) return displaySignalToken(token);
      return token.toLowerCase().startsWith("validation") ? token.toLowerCase() : titleCase(token);
    });

    return truncateAtWord(formatted.join(" · "), 58);
  }

  return truncateAtWord(normalized.replace(/\s+task\b.*$/i, ""), 58);
}

function decisionRiskSignal(decision) {
  const explicitRisk = compactSignalText(decision?.risk_signal);
  if (explicitRisk) return conciseRiskSignal(explicitRisk, decision);

  const framework = clean(decision?.affected_framework || decision?.framework_impact);
  const connector = clean(decision?.affected_connector);
  const taskStatus = clean(decision?.task_status);
  const validationStatus = clean(decision?.validation_status);
  const recommendation = clean(decision?.recommendation);

  const fromStructuredFields = conciseRiskFromParts(framework, connector, validationStatus, taskStatus);
  if (fromStructuredFields) return fromStructuredFields;

  return recommendation
    ? truncateAtWord(`${recommendation} reduces operational uncertainty before action is taken.`, 58)
    : "Operational risk context is not yet specific enough.";
}

function extractStateFromSignal(value, keyword, fallback = "") {
  const text = clean(value);
  if (!text) return fallback;

  const escapedKeyword = keyword.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const pattern = new RegExp(`${escapedKeyword}\\s+(?:state\\s+)?(?:is|=)\\s+([a-zA-Z0-9_ -]+)`, "i");
  const match = text.match(pattern);
  if (!match) return fallback;

  return clean(match[1]).replace(/[.;:].*$/, "").trim() || fallback;
}

function firstSentence(value) {
  const text = clean(value).replace(/\s+/g, " ");
  if (!text) return "";

  const sentence = text.match(/^.*?[.!?](?:\s|$)/);
  return clean(sentence?.[0] || text);
}

function signalSummaryValue(signal, decision) {
  const label = clean(signal?.label).toLowerCase();
  const value = clean(signal?.value);

  if (!value) return "—";

  if (label === "task") {
    const rule = clean(decision?.rule_id || decision?.affected_rule || decision?.finding_id);
    const resource = clean(decision?.resource_id || decision?.affected_resource);
    const framework = displaySignalToken(decision?.affected_framework || decision?.framework_impact);
    const connector = displaySignalToken(decision?.affected_connector);

    if (rule && resource) return truncateAtWord(`${rule} on ${resource}`, 64);
    if (rule) return truncateAtWord(rule, 64);

    const firstClause = value.split(/\s+is\s+/i)[0];
    if (clean(firstClause) && clean(firstClause).length < value.length) {
      return truncateAtWord(firstClause, 64);
    }

    if (framework && connector) return `${framework} · ${connector}`;
    return truncateAtWord(firstSentence(value), 64);
  }

  if (label === "validation") {
    const status = clean(decision?.validation_status) || extractStateFromSignal(value, "Validation");
    if (status) return humanizeDisplayValue(status);
    if (value.toLowerCase().includes("failed")) return "Failed";
    if (value.toLowerCase().includes("passed")) return "Passed";
    if (value.toLowerCase().includes("stale")) return "Stale";
    return truncateAtWord(firstSentence(value), 48);
  }

  if (label === "workflow") {
    const status = clean(decision?.workflow_status) || extractStateFromSignal(value, "Workflow");
    if (status) return humanizeDisplayValue(status);
    if (value.toLowerCase().includes("queued")) return "Queued";
    if (value.toLowerCase().includes("failed")) return "Failed";
    if (value.toLowerCase().includes("blocked")) return "Blocked";
    return truncateAtWord(firstSentence(value), 48);
  }

  if (label === "evidence") {
    const state = clean(decision?.evidence_state) || extractStateFromSignal(value, "Evidence");
    if (state) return humanizeDisplayValue(state);

    const target = clean(decision?.evidence_target);
    if (target) return truncateAtWord(target, 64);

    if (value.toLowerCase().includes("linked")) return "Linked";
    if (value.toLowerCase().includes("missing")) return "Missing";
    return truncateAtWord(firstSentence(value), 48);
  }

  if (label === "risk") {
    return conciseRiskSignal(value, decision) || truncateAtWord(firstSentence(value), 58);
  }

  return truncateAtWord(firstSentence(value), 58);
}

function signalDetailText(signal) {
  return clean(signal?.value) || "No additional signal detail is available.";
}

function decisionContextSignals(signals, decision) {
  const rows = [];
  const seen = new Set();

  (signals || []).forEach((signal) => {
    const label = clean(signal?.label);
    const value = signalDetailText(signal);
    const key = `${label.toLowerCase()}::${value.toLowerCase()}`;
    if (!label || !value || seen.has(key)) return;

    seen.add(key);
    rows.push({
      label,
      value,
      help: clean(signal?.help) || signalHelpText(label, value),
    });
  });

  const extraFields = [
    ["Evidence Target", decision?.evidence_target],
    ["Evidence Source", decision?.evidence_source],
    ["Evidence Gap", decision?.evidence_gap_reason],
    ["Validation Blocker", decision?.validation_blocker],
    ["Workflow Transition", decision?.workflow_transition_issue],
    ["Risk Summary", decision?.risk_summary],
    ["Operator Next Step", decision?.operator_next_step],
  ];

  extraFields.forEach(([label, value]) => {
    const text = clean(value);
    const key = `${String(label).toLowerCase()}::${text.toLowerCase()}`;
    if (!text || seen.has(key)) return;

    seen.add(key);
    rows.push({
      label,
      value: text,
      help: signalHelpText(label, text),
    });
  });

  return rows;
}

function normalizeDecisionSignal(signal) {
  if (typeof signal === "string") {
    const value = compactSignalText(signal);
    if (!value) return null;
    const label = operationalSignalLabel("", value);
    return {
      label,
      value,
      help: signalHelpText(label, value),
    };
  }

  if (!signal || typeof signal !== "object") return null;

  const rawValue = clean(signal.value || signal.message || signal.reason || signal.summary);
  const value = compactSignalText(rawValue);
  if (!value) return null;

  const label = operationalSignalLabel(signal.label || signal.name, value);

  return {
    label,
    value,
    help: clean(signal.help || signal.hint || signal.status) || signalHelpText(label, value),
  };
}

function decisionSupportingSignals(decision) {
  const directSignals = Array.isArray(decision?.supporting_signals) ? decision.supporting_signals : [];
  const normalizedSignals = directSignals
    .map(normalizeDecisionSignal)
    .filter(Boolean)
    .filter((signal) => signal.label !== "Signal");

  if (normalizedSignals.length) {
    return normalizedSignals.slice(0, 6);
  }

  const fallbackSignals = [
    {
      label: "Validation",
      value: compactSignalText(
        decision?.validation_signal || decision?.reasoning?.validation_state || decision?.validation_status,
        "No validation signal is linked yet.",
      ),
    },
    {
      label: "Workflow",
      value: compactSignalText(
        decision?.workflow_signal || decision?.reasoning?.workflow_state || decision?.workflow_status,
        "No workflow signal is linked yet.",
      ),
    },
    {
      label: "Evidence",
      value: compactSignalText(
        decision?.evidence_signal || decision?.evidence_state,
        "No specific evidence target is linked yet.",
      ),
    },
    {
      label: "Risk",
      value: decisionRiskSignal(decision),
    },
  ]
    .filter((signal) => clean(signal.value))
    .map((signal) => ({
      ...signal,
      help: signalHelpText(signal.label, signal.value),
    }));

  return fallbackSignals.slice(0, 4);
}

function decisionPrimaryReason(decision) {
  const candidates = [
    decision?.short_reason,
    decision?.why_now,
    decision?.operator_guidance,
    decision?.reasoning_summary,
    decision?.reason,
  ];

  const combined = candidates.map(clean).filter(Boolean).join(" ");
  const transitionSummary = transitionSummaryFromText(combined);
  const primary = candidates.map(clean).find(Boolean);

  if (transitionSummary && clean(primary).toLowerCase().includes("workflow recovery")) {
    return transitionSummary;
  }

  if (primary && primary.toLowerCase().includes("recommended action:")) {
    return primary.split(/recommended action:/i)[0].trim().replace(/[.]*$/, ".") || primary;
  }

  return operatorFriendlyWorkflowText(primary) || "Decision Support has identified an operator-controlled advisory action.";
}

function decisionWhyNow(decision) {
  return (
    operatorFriendlyWorkflowText(decision?.why_now) ||
    operatorFriendlyWorkflowText(decision?.context_summary) ||
    operatorFriendlyWorkflowText(decision?.operator_guidance) ||
    operatorFriendlyWorkflowText(decision?.reason) ||
    "This recommendation combines task, validation, workflow, evidence, and risk context before any action is taken."
  );
}

function mergeDecisionForDisplay(decision, detail) {
  if (!decision) return null;
  const detailDecision = detail?.decision || detail?.compact_decision || null;
  if (!detailDecision || !sameDecision(decision, detailDecision)) return decision;
  return { ...decision, ...detailDecision };
}

function DecisionContextDrawer({
  open,
  decision,
  selectedSignal,
  contextSignals,
  actionSteps,
  detail,
  onClose,
}) {
  if (!open || !decision) return null;

  const selectedLabel = clean(selectedSignal?.label);
  const selectedDetail = selectedSignal || contextSignals[0] || null;
  const detailFeedback = Array.isArray(detail?.feedback) ? detail.feedback : [];
  const detailHistory = Array.isArray(detail?.history) ? detail.history : [];
  const auditAvailable = Boolean(detail);

  return (
    <div className="mec-decision-context-layer" role="presentation">
      <button
        type="button"
        className="mec-decision-context-backdrop"
        aria-label="Close decision context"
        onClick={onClose}
      />
      <aside
        className="mec-decision-context-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Decision Context"
      >
        <button
          type="button"
          className="mec-decision-context-floating-close"
          aria-label="Close decision context"
          onClick={onClose}
        >
          ×
        </button>
        <div className="mec-decision-context-header">
          <div>
            <span>Decision Context</span>
            <h3>{decision.recommendation || "Decision Support"}</h3>
            <p>{decisionStatusText(decision)}</p>
          </div>
          <button
            type="button"
            className="mec-decision-context-close-button"
            onClick={onClose}
            aria-label="Close decision context"
          >
            Close
          </button>
        </div>

        <div className="mec-decision-context-body">
          <button
            type="button"
            className="mec-decision-context-inline-close"
            onClick={onClose}
            aria-label="Back to Decision Support"
          >
            ← Back to Decision Support
          </button>

          {selectedDetail ? (
            <section className="mec-decision-context-focus">
              <span>{selectedLabel || "Selected Context"}</span>
              <strong>{selectedDetail.value}</strong>
              <small>{selectedDetail.help || signalHelpText(selectedDetail.label, selectedDetail.value)}</small>
            </section>
          ) : null}

          <section className="mec-decision-context-section">
            <span>Why Now</span>
            <p>{decisionWhyNow(decision)}</p>
          </section>

          <section className="mec-decision-context-section">
            <span>Recommended Operator Path</span>
            <ol>
              {actionSteps.map((step, index) => (
                <li key={`drawer-step-${index}`}>{step}</li>
              ))}
            </ol>
          </section>

          {contextSignals.length ? (
            <section className="mec-decision-context-section">
              <span>Supporting Context</span>
              <div className="mec-decision-context-list">
                {contextSignals.map((signal, index) => (
                  <div
                    key={`drawer-context-${signal.label}-${index}`}
                    className={
                      clean(selectedDetail?.label).toLowerCase() === clean(signal.label).toLowerCase()
                        ? "active"
                        : ""
                    }
                  >
                    <strong>{signal.label}</strong>
                    <small>{signal.value}</small>
                  </div>
                ))}
              </div>
            </section>
          ) : null}

          {auditAvailable ? (
            <section className="mec-decision-context-section">
              <span>Decision Audit</span>
              <div className="mec-decision-context-audit">
                <div>
                  <strong>{detail?.payload_shape === "persisted_detail" ? "Persisted decision detail" : "Decision detail"}</strong>
                  <small>Payload shape</small>
                </div>
                <div>
                  <strong>{detailFeedback.length}</strong>
                  <small>Feedback record(s)</small>
                </div>
                <div>
                  <strong>{detailHistory.length}</strong>
                  <small>History record(s)</small>
                </div>
                <div>
                  <strong>No</strong>
                  <small>Autonomous execution</small>
                </div>
              </div>
            </section>
          ) : null}
        </div>
      </aside>
    </div>
  );
}

function DecisionSupportPanel({
  loading,
  error,
  decisions,
  selectedDecision,
  selectedWorkItemId,
  detail,
  onRefresh,
  onOpenDetail,
  onFeedback,
  onOpenDecisionWorkbench,
  feedbackLoading,
}) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerSignal, setDrawerSignal] = useState(null);
  const activeDecision = mergeDecisionForDisplay(selectedDecision || decisions[0] || null, detail);
  const activeDecisionId = clean(activeDecision?.decision_id);
  const detailFeedback = Array.isArray(detail?.feedback) ? detail.feedback : [];
  const detailHistory = Array.isArray(detail?.history) ? detail.history : [];
  const actionSteps = decisionActionSteps(activeDecision);
  const supportingSignals = decisionSupportingSignals(activeDecision);
  const summarySignals = supportingSignals.slice(0, 4);
  const contextSignals = decisionContextSignals(supportingSignals, activeDecision);
  const affectedTaskId = decisionAffectedTaskId(activeDecision);
  const selectedTaskId = clean(selectedWorkItemId);
  const otherRecommendations = decisions
    .filter((decision) => !sameDecision(decision, activeDecision))
    .filter((decision) => {
      if (!selectedTaskId) return false;
      return decisionAffectedTaskId(decision) === selectedTaskId;
    })
    .slice(0, 3);

  const openContextDrawer = useCallback((signal = null) => {
    setDrawerSignal(signal || contextSignals[0] || null);
    setDrawerOpen(true);
  }, [contextSignals]);

  const closeContextDrawer = useCallback(() => {
    setDrawerOpen(false);
    setDrawerSignal(null);
  }, []);

  const openReasoningDrawer = useCallback(async () => {
    if (!activeDecision) return;
    await onOpenDetail(activeDecision);
    setDrawerSignal(null);
    setDrawerOpen(true);
  }, [activeDecision, onOpenDetail]);

  return (
    <section className="mec-recommended-action neutral" aria-label="Decision Support">
      <div>
        <span>Decision Support</span>
        <h3>{activeDecision?.recommendation || "No decision support recommendation available."}</h3>
        <p>
          {activeDecision
            ? decisionPrimaryReason(activeDecision)
            : "OASYS will surface advisory recommendations here after Decision Support loads. No autonomous action is executed."}
        </p>

        {activeDecision ? (
          <div className="mec-action-buttons">
            <button
              type="button"
              className="mec-secondary-button"
              onClick={openReasoningDrawer}
              disabled={Boolean(feedbackLoading)}
            >
              {feedbackLoading === `${activeDecisionId}:detail` ? "Loading…" : "View Reasoning"}
            </button>
            <button
              type="button"
              className="mec-secondary-button"
              onClick={() => onOpenDecisionWorkbench?.(activeDecision)}
              disabled={Boolean(feedbackLoading)}
            >
              View In Workbench
            </button>
            <button
              type="button"
              className="mec-secondary-button"
              onClick={() => onFeedback(activeDecision, "useful")}
              disabled={Boolean(feedbackLoading)}
            >
              {feedbackLoading === `${activeDecisionId}:useful` ? "Recording…" : "Mark Useful"}
            </button>
            <button
              type="button"
              className="mec-secondary-button"
              onClick={() => onFeedback(activeDecision, "accepted")}
              disabled={Boolean(feedbackLoading)}
            >
              {feedbackLoading === `${activeDecisionId}:accepted` ? "Accepting…" : "Accept Advisory"}
            </button>
            <button
              type="button"
              className="mec-secondary-button"
              onClick={() => onFeedback(activeDecision, "rejected")}
              disabled={Boolean(feedbackLoading)}
            >
              {feedbackLoading === `${activeDecisionId}:rejected` ? "Rejecting…" : "Reject"}
            </button>
          </div>
        ) : null}

        {error ? <p className="mec-error">{error}</p> : null}
      </div>

      <div>
        <StatusPill tone={activeDecision ? decisionPriorityTone(activeDecision) : "neutral"}>
          {activeDecision ? decisionStatusText(activeDecision) : loading ? "Loading" : "Advisory"}
        </StatusPill>

        {loading ? (
          <ul>
            <li>Loading Decision Support recommendations…</li>
          </ul>
        ) : activeDecision ? (
          <>
            <div className="mec-workflow-next-step">
              <span>Why Now</span>
              <strong>{decisionWhyNow(activeDecision)}</strong>
            </div>

            <div className="mec-validation-section">
              <span>Recommended Operator Path</span>
              <ol>
                {actionSteps.map((step, index) => (
                  <li key={`decision-step-${index}`}>{step}</li>
                ))}
              </ol>
            </div>

            {summarySignals.length ? (
              <div className="mec-context-metrics mec-validation-metric-grid">
                {summarySignals.map((signal, index) => (
                  <button
                    type="button"
                    className="mec-decision-signal-card"
                    key={`decision-signal-${index}`}
                    title={signalDetailText(signal)}
                    onClick={() => openContextDrawer(signal)}
                    aria-label={`Open ${signal.label} decision context`}
                  >
                    <span>{signal.label}</span>
                    <strong>{signalSummaryValue(signal, activeDecision)}</strong>
                    <small>{signal.help || "supporting context"}</small>
                  </button>
                ))}
              </div>
            ) : null}
          </>
        ) : (
          <ul>
            <li>No Decision Support recommendations are currently available for this workspace.</li>
          </ul>
        )}

        <div className="mec-action-buttons">
          <button type="button" className="mec-link-button" onClick={onRefresh} disabled={loading}>
            Refresh Decisions
          </button>
          {activeDecision && affectedTaskId ? (
            <button
              type="button"
              className="mec-link-button"
              onClick={() => onOpenDecisionWorkbench?.(activeDecision)}
              disabled={Boolean(feedbackLoading)}
            >
              Open In Workbench
            </button>
          ) : null}
        </div>

        {detail ? (
          <div className="mec-compact-notes">
            <span>Decision Audit</span>
            <div>
              <strong>{detail?.payload_shape === "persisted_detail" ? "Persisted decision detail" : "Decision detail"}</strong>
              <small>
                Feedback {detailFeedback.length} · History {detailHistory.length} · No autonomous execution
              </small>
            </div>
          </div>
        ) : null}

        {otherRecommendations.length ? (
          <div className="mec-compact-notes">
            <span>Other Recommendations For This Task</span>
            {otherRecommendations.map((decision) => (
              <button
                type="button"
                className="mec-link-button"
                key={decision.decision_id}
                onClick={() => onOpenDetail(decision)}
              >
                {titleCase(decision.decision_type)} · {decision.recommendation}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      <DecisionContextDrawer
        open={drawerOpen}
        decision={activeDecision}
        selectedSignal={drawerSignal}
        contextSignals={contextSignals}
        actionSteps={actionSteps}
        detail={detail}
        onClose={closeContextDrawer}
      />
    </section>
  );
}


export default function MissionExecutionCenter({
  apiBaseUrl,
  getAccessToken,
  userProfile = null,
  routeParams = {},
  setMode,
  themeMode = "light",
}) {
  const {
    setCopilotContext,
    clearCopilotContext,
  } = useAdvisor();

  const [loading, setLoading] = useState(true);
  const [workingQueue, setWorkingQueue] = useState(false);
  const [err, setErr] = useState("");
  const [analytics, setAnalytics] = useState(null);
  const [queue, setQueue] = useState([]);
  const [selectedId, setSelectedId] = useState(routeParams?.work_item_id || routeParams?.workItemId || "");
  const [workspace, setWorkspace] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [activeFilter, setActiveFilter] = useState(routeParams?.queue_group || routeParams?.queueGroup || "ai_priority");
  const [frameworkFilter, setFrameworkFilter] = useState(routeParams?.framework || "all");
  const [query, setQuery] = useState("");
  const [activeTab, setActiveTab] = useState("evidence");
  const [taskExecutionModal, setTaskExecutionModal] = useState({ open: false, initialTab: "overview" });
  const [actionLoading, setActionLoading] = useState("");
  const [actionMessage, setActionMessage] = useState("");
  const [validationRerunResult, setValidationRerunResult] = useState(null);
  const [validationRunHistory, setValidationRunHistory] = useState([]);
  const [evidenceContext, setEvidenceContext] = useState(null);
  const [evidenceContextLoading, setEvidenceContextLoading] = useState(false);
  const [evidenceContextErr, setEvidenceContextErr] = useState("");
  const [workflowContext, setWorkflowContext] = useState(null);
  const [workflowContextLoading, setWorkflowContextLoading] = useState(false);
  const [workflowContextErr, setWorkflowContextErr] = useState("");
  const [sopContext, setSopContext] = useState(null);
  const [sopContextLoading, setSopContextLoading] = useState(false);
  const [sopContextErr, setSopContextErr] = useState("");
  const [remediationGuidanceContext, setRemediationGuidanceContext] = useState(null);
  const [remediationGuidanceLoading, setRemediationGuidanceLoading] = useState(false);
  const [remediationGuidanceErr, setRemediationGuidanceErr] = useState("");
  const [blockConfirmation, setBlockConfirmation] = useState("");
  const [operatorNote, setOperatorNote] = useState("");
  const [decisionSupportLoading, setDecisionSupportLoading] = useState(false);
  const [decisionSupportErr, setDecisionSupportErr] = useState("");
  const [decisionSupport, setDecisionSupport] = useState(null);
  const [decisionDetail, setDecisionDetail] = useState(null);
  const [decisionActionLoading, setDecisionActionLoading] = useState("");
  const [workOptimizationContext, setWorkOptimizationContext] = useState(null);
  const [workOptimizationLoading, setWorkOptimizationLoading] = useState(false);
  const [workOptimizationErr, setWorkOptimizationErr] = useState("");
  const [taskOptimizationContext, setTaskOptimizationContext] = useState(null);
  const [taskOptimizationLoading, setTaskOptimizationLoading] = useState(false);
  const [taskOptimizationErr, setTaskOptimizationErr] = useState("");
  const [executionPlanContext, setExecutionPlanContext] = useState(null);
  const [executionPlanLoading, setExecutionPlanLoading] = useState(false);
  const [executionPlanErr, setExecutionPlanErr] = useState("");
  const [taskLearningContext, setTaskLearningContext] = useState(null);
  const [taskLearningLoading, setTaskLearningLoading] = useState(false);
  const [taskLearningErr, setTaskLearningErr] = useState("");
  const [taskLearningRefreshState, setTaskLearningRefreshState] = useState("idle");
  const [taskIntelligence, setTaskIntelligence] = useState(null);
  const [taskIntelligenceLoading, setTaskIntelligenceLoading] = useState(false);
  const [taskIntelligenceErr, setTaskIntelligenceErr] = useState("");
  const [taskIntelligenceQuestion, setTaskIntelligenceQuestion] = useState("");
  const [pageIntelligence, setPageIntelligence] = useState({
    question: "What should I work on first?",
    card: null,
    metadata: null,
    loading: false,
    error: "",
  });
  const [executionPlanningSummary, setExecutionPlanningSummary] = useState(null);
  const [executionPlanningSummaryLoading, setExecutionPlanningSummaryLoading] = useState(false);
  const [executionPlanningSummaryErr, setExecutionPlanningSummaryErr] = useState("");
  const [learningSummaryContext, setLearningSummaryContext] = useState(null);
  const [learningSummaryLoading, setLearningSummaryLoading] = useState(false);
  const [learningSummaryErr, setLearningSummaryErr] = useState("");
  const [learningSummaryRefreshState, setLearningSummaryRefreshState] = useState("idle");
  const workbenchRef = useRef(null);
  const activeWorkRef = useRef(null);
  const lastAutoSelectionRef = useRef("");
  const taskLearningRefreshResetRef = useRef(null);
  const learningSummaryRefreshResetRef = useRef(null);

  const normalizedBase = useMemo(() => {
    const base = clean(apiBaseUrl);
    return base.endsWith("/") ? base.slice(0, -1) : base;
  }, [apiBaseUrl]);

  const tenantId = clean(
    userProfile?.tenant_id ||
      userProfile?.tenantId ||
      userProfile?.app_metadata?.tenant_id ||
      process.env.REACT_APP_TENANT_ID
  );

  const userId = clean(
    userProfile?.user_id ||
      userProfile?.userId ||
      userProfile?.id ||
      userProfile?.app_metadata?.user_id ||
      process.env.REACT_APP_USER_ID
  );

  const userName = clean(userProfile?.name || userProfile?.email || "Operator");

  const requestJson = useCallback(
    async (path, { method = "GET", body, params, signal } = {}) => {
      if (!tenantId || !userId) {
        throw new Error("Mission Execution requires tenant and user identifiers.");
      }

      const token = await getAccessToken();
      if (!token) return null;

      const search = new URLSearchParams();
      Object.entries(params || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") search.set(key, String(value));
      });

      const url = `${normalizedBase}${path}${search.toString() ? `?${search.toString()}` : ""}`;
      const res = await fetch(url, {
        method,
        signal,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Tenant-Id": tenantId,
          "X-User-Id": userId,
          "X-User-Name": userName,
        },
        body: body ? JSON.stringify(body) : undefined,
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`${res.status} ${res.statusText}: ${text}`);
      }

      return res.json();
    },
    [getAccessToken, normalizedBase, tenantId, userId, userName]
  );

  const loadPageIntelligence = useCallback(
    async (
      question = "What should I work on first?",
      signal
    ) => {
      const normalizedQuestion =
        clean(question) || "What should I work on first?";

      setPageIntelligence((current) => ({
        ...current,
        question: normalizedQuestion,
        loading: true,
        error: "",
      }));

      try {
        const framework =
          frameworkFilter && frameworkFilter !== "all"
            ? frameworkFilter
            : undefined;

        const payload = await requestJson("/ai/copilot/ask", {
          method: "POST",
          signal,
          body: {
            question: normalizedQuestion,
            scope: "mission_execution",
            copilot_context: {
              page: "mission_execution",
              surface: "mission_execution_page",
              intelligence_mode: "page",
              ...(framework ? { framework } : {}),
            },
          },
        });

        const card =
          payload?.card && typeof payload.card === "object"
            ? payload.card
            : null;

        if (!card) {
          throw new Error(
            "OASYS Intelligence returned no grounded Mission Execution page card."
          );
        }

        setPageIntelligence({
          question: normalizedQuestion,
          card,
          metadata:
            card?.metadata && typeof card.metadata === "object"
              ? card.metadata
              : payload?.metadata || null,
          loading: false,
          error: "",
        });

        return payload || null;
      } catch (error) {
        if (error?.name !== "AbortError") {
          setPageIntelligence((current) => ({
            ...current,
            loading: false,
            error:
              "OASYS Intelligence is temporarily unavailable. "
              + "The deterministic Mission Execution queue remains available.",
          }));
        }
        return null;
      } finally {
        if (!signal?.aborted) {
          setPageIntelligence((current) => ({
            ...current,
            loading: false,
          }));
        }
      }
    },
    [frameworkFilter, requestJson]
  );

  const loadTaskIntelligence = useCallback(
    async (
      taskId,
      question = "Why is OASYS telling me to do this first?",
      signal
    ) => {
      const normalizedTaskId = normalizeTaskId(taskId);
      const normalizedQuestion = clean(question) || "Why is OASYS telling me to do this first?";

      if (!normalizedTaskId) {
        setTaskIntelligence(null);
        setTaskIntelligenceErr("");
        return null;
      }

      setTaskIntelligenceLoading(true);
      setTaskIntelligenceErr("");
      setTaskIntelligenceQuestion(normalizedQuestion);

      try {
        const payload = await requestJson("/ai/copilot/ask", {
          method: "POST",
          signal,
          body: {
            question: normalizedQuestion,
            scope: "mission_execution",
            record_id: normalizedTaskId,
            copilot_context: {
              page: "mission_execution",
              surface: "mission_execution",
              task_id: normalizedTaskId,
            },
          },
        });

        setTaskIntelligence(payload || null);
        return payload || null;
      } catch (error) {
        if (error?.name !== "AbortError") {
          setTaskIntelligenceErr(
            "OASYS Intelligence is temporarily unavailable. Operational data and deterministic guidance remain available."
          );
        }
        return null;
      } finally {
        if (!signal?.aborted) {
          setTaskIntelligenceLoading(false);
        }
      }
    },
    [requestJson]
  );

  const loadExecutionPlanningSummary = useCallback(
    async (signal) => {
      setExecutionPlanningSummaryLoading(true);
      setExecutionPlanningSummaryErr("");

      try {
        const payload = await requestJson("/execution-plans/summary", {
          signal,
        });
        setExecutionPlanningSummary(payload || null);
        return payload || null;
      } catch (error) {
        if (error?.name !== "AbortError") {
          const message = String(error?.message || error || "Execution planning summary failed to load.");
          setExecutionPlanningSummaryErr(message);
          console.warn("Execution planning summary failed to load", error);
        }
        return null;
      } finally {
        setExecutionPlanningSummaryLoading(false);
      }
    },
    [requestJson]
  );

  const loadLearningSummary = useCallback(
    async (signal, options = {}) => {
      const manualRefresh = options?.manualRefresh === true;
      const refreshStartedAt = Date.now();

      if (learningSummaryRefreshResetRef.current) {
        window.clearTimeout(learningSummaryRefreshResetRef.current);
        learningSummaryRefreshResetRef.current = null;
      }

      setLearningSummaryLoading(true);
      setLearningSummaryErr("");
      if (manualRefresh) setLearningSummaryRefreshState("refreshing");

      let succeeded = false;

      try {
        const payload = await requestJson("/learning/summary", {
          signal,
        });
        setLearningSummaryContext(payload || null);
        succeeded = true;
        return payload || null;
      } catch (error) {
        if (error?.name !== "AbortError") {
          const message = String(error?.message || error || "Learning summary failed to load.");
          setLearningSummaryErr(message);
          setLearningSummaryRefreshState(manualRefresh ? "error" : "idle");
          console.warn("Learning summary failed to load", error);
        }
        return null;
      } finally {
        if (manualRefresh) {
          const elapsedMs = Date.now() - refreshStartedAt;
          const remainingMs = Math.max(0, LEARNING_REFRESH_MIN_VISIBLE_MS - elapsedMs);
          if (remainingMs > 0) {
            await new Promise((resolve) => window.setTimeout(resolve, remainingMs));
          }
        }

        setLearningSummaryLoading(false);

        if (manualRefresh) {
          setLearningSummaryRefreshState(succeeded ? "success" : "error");
          learningSummaryRefreshResetRef.current = window.setTimeout(() => {
            setLearningSummaryRefreshState("idle");
            learningSummaryRefreshResetRef.current = null;
          }, LEARNING_REFRESH_SUCCESS_VISIBLE_MS);
        }
      }
    },
    [requestJson]
  );

  const loadDecisionSupport = useCallback(
    async (signal, params = {}) => {
      setDecisionSupportLoading(true);
      setDecisionSupportErr("");

      try {
        const payload = await requestJson("/decision-support", {
          signal,
          params: {
            surface: "mission_execution",
            limit: 5,
            ...params,
          },
        });

        setDecisionSupport(payload || null);
        return payload || null;
      } catch (error) {
        if (error?.name !== "AbortError") {
          setDecisionSupportErr(String(error?.message || error || "Decision Support failed to load."));
        }
        return null;
      } finally {
        setDecisionSupportLoading(false);
      }
    },
    [requestJson]
  );

  const loadWorkOptimizationContext = useCallback(
    async (signal) => {
      setWorkOptimizationLoading(true);
      setWorkOptimizationErr("");

      try {
        const payload = await requestJson("/work-optimization/opportunities", {
          signal,
          params: { limit: 100 },
        });
        setWorkOptimizationContext(payload || null);
        return payload || null;
      } catch (error) {
        if (error?.name !== "AbortError") {
          const message = String(error?.message || error || "Work Optimization failed to load.");
          setWorkOptimizationErr(message);
          console.warn("Work Optimization failed to load", error);
        }
        return null;
      } finally {
        setWorkOptimizationLoading(false);
      }
    },
    [requestJson]
  );

  const loadTaskOptimizationContext = useCallback(
    async (taskId, signal) => {
      const cleanedTaskId = clean(taskId);
      if (!cleanedTaskId) {
        setTaskOptimizationContext(null);
        setTaskOptimizationErr("");
        return null;
      }

      setTaskOptimizationLoading(true);
      setTaskOptimizationErr("");

      try {
        const payload = await requestJson(`/work-optimization/task/${encodeURIComponent(cleanedTaskId)}`, {
          signal,
          params: { limit: 100 },
        });
        setTaskOptimizationContext(payload || null);
        return payload || null;
      } catch (error) {
        if (error?.name !== "AbortError") {
          const message = String(error?.message || error || "Task optimization context failed to load.");
          setTaskOptimizationErr(message);
          console.warn("Task optimization context failed to load", error);
        }
        return null;
      } finally {
        setTaskOptimizationLoading(false);
      }
    },
    [requestJson]
  );

  const loadExecutionPlanContext = useCallback(
    async (taskId, signal) => {
      const cleanedTaskId = clean(taskId);
      if (!cleanedTaskId) {
        setExecutionPlanContext(null);
        setExecutionPlanErr("");
        return null;
      }

      setExecutionPlanLoading(true);
      setExecutionPlanErr("");

      try {
        const payload = await requestJson(`/execution-plans/task/${encodeURIComponent(cleanedTaskId)}`, {
          signal,
          params: { limit: 100 },
        });
        setExecutionPlanContext(payload || null);
        return payload || null;
      } catch (error) {
        if (error?.name !== "AbortError") {
          const message = String(error?.message || error || "Task execution plan failed to load.");
          setExecutionPlanErr(message);
          console.warn("Task execution plan failed to load", error);
        }
        return null;
      } finally {
        setExecutionPlanLoading(false);
      }
    },
    [requestJson]
  );

  const loadTaskLearningContext = useCallback(
    async (taskId, signal, options = {}) => {
      const cleanedTaskId = clean(taskId);
      const manualRefresh = options?.manualRefresh === true;
      const refreshStartedAt = Date.now();

      if (taskLearningRefreshResetRef.current) {
        window.clearTimeout(taskLearningRefreshResetRef.current);
        taskLearningRefreshResetRef.current = null;
      }

      if (!cleanedTaskId) {
        setTaskLearningContext(null);
        setTaskLearningErr("");
        setTaskLearningRefreshState("idle");
        return null;
      }

      setTaskLearningLoading(true);
      setTaskLearningErr("");
      if (manualRefresh) setTaskLearningRefreshState("refreshing");

      let succeeded = false;

      try {
        const [taskPayload, patternsPayload] = await Promise.all([
          requestJson(`/learning/task/${encodeURIComponent(cleanedTaskId)}`, { signal }),
          requestJson("/learning/patterns", { signal }),
        ]);

        const mergedPayload = taskPayload
          ? {
              ...taskPayload,
              connector_patterns: patternsPayload?.connector_patterns || taskPayload.connector_patterns || {},
              framework_patterns: patternsPayload?.framework_patterns || taskPayload.framework_patterns || {},
              common_blockers: patternsPayload?.common_blockers || taskPayload.common_blockers || [],
              global_patterns: patternsPayload?.patterns || [],
              rollback_patterns: patternsPayload?.rollback_patterns || null,
              validation_patterns: patternsPayload?.validation_patterns || null,
            }
          : null;

        setTaskLearningContext(mergedPayload);
        succeeded = true;
        return mergedPayload;
      } catch (error) {
        if (error?.name !== "AbortError") {
          const message = String(error?.message || error || "Task learning insights failed to load.");
          setTaskLearningErr(message);
          setTaskLearningRefreshState(manualRefresh ? "error" : "idle");
          console.warn("Task learning insights failed to load", error);
        }
        return null;
      } finally {
        if (manualRefresh) {
          const elapsedMs = Date.now() - refreshStartedAt;
          const remainingMs = Math.max(0, LEARNING_REFRESH_MIN_VISIBLE_MS - elapsedMs);
          if (remainingMs > 0) {
            await new Promise((resolve) => window.setTimeout(resolve, remainingMs));
          }
        }

        setTaskLearningLoading(false);

        if (manualRefresh) {
          setTaskLearningRefreshState(succeeded ? "success" : "error");
          taskLearningRefreshResetRef.current = window.setTimeout(() => {
            setTaskLearningRefreshState("idle");
            taskLearningRefreshResetRef.current = null;
          }, LEARNING_REFRESH_SUCCESS_VISIBLE_MS);
        }
      }
    },
    [requestJson]
  );

  const loadWorkspace = useCallback(
    async (workItemId, signal) => {
      if (!workItemId) return;
      const encodedId = encodeURIComponent(workItemId);
      const detail = await requestJson(`/execution/work-item/${encodedId}`, { signal });
      const history = await requestJson("/execution/timeline", {
        signal,
        params: { work_item_id: workItemId },
      });

      setWorkspace(detail || null);
      setTimeline(normalizeItems(history));
    },
    [requestJson]
  );

  const loadValidationRunHistory = useCallback(
    async (taskId, signal) => {
      const cleanedTaskId = clean(taskId);
      if (!cleanedTaskId) {
        setValidationRunHistory([]);
        return [];
      }

      try {
        const payload = await requestJson("/validation/runs", {
          signal,
          params: {
            task_id: cleanedTaskId,
            limit: 50,
          },
        });
        const rows = normalizeItems(payload);
        setValidationRunHistory(rows);
        return rows;
      } catch (error) {
        if (error?.name !== "AbortError") {
          // Validation history is supporting context only. Do not fail the whole workbench if it is unavailable.
          console.warn("Validation run history failed to load", error);
        }
        return [];
      }
    },
    [requestJson]
  );

  const loadTaskEvidenceContext = useCallback(
    async (taskId, signal) => {
      const cleanedTaskId = clean(taskId);
      if (!cleanedTaskId) {
        setEvidenceContext(null);
        setEvidenceContextErr("");
        return null;
      }

      setEvidenceContextLoading(true);
      setEvidenceContextErr("");

      try {
        const payload = await requestJson(`/evidence/task-context/${encodeURIComponent(cleanedTaskId)}`, {
          signal,
          params: { limit: 75 },
        });
        setEvidenceContext(payload || null);
        return payload || null;
      } catch (error) {
        if (error?.name !== "AbortError") {
          const message = String(error?.message || error || "Task evidence context failed to load.");
          setEvidenceContextErr(message);
          console.warn("Task evidence context failed to load", error);
        }
        return null;
      } finally {
        setEvidenceContextLoading(false);
      }
    },
    [requestJson]
  );



  const loadTaskWorkflowContext = useCallback(
    async (taskId, signal) => {
      const cleanedTaskId = clean(taskId);
      if (!cleanedTaskId) {
        setWorkflowContext(null);
        setWorkflowContextErr("");
        return null;
      }

      setWorkflowContextLoading(true);
      setWorkflowContextErr("");

      try {
        const payload = await requestJson(`/workflows/task-context/${encodeURIComponent(cleanedTaskId)}`, {
          signal,
          params: {
            tenant_id: tenantId,
            limit: 100,
          },
        });
        setWorkflowContext(payload || null);
        return payload || null;
      } catch (error) {
        if (error?.name !== "AbortError") {
          const message = String(error?.message || error || "Task workflow context failed to load.");
          setWorkflowContextErr(message);
          console.warn("Task workflow context failed to load", error);
        }
        return null;
      } finally {
        setWorkflowContextLoading(false);
      }
    },
    [requestJson, tenantId]
  );

  const loadTaskSOPContext = useCallback(
    async (taskId, signal) => {
      const cleanedTaskId = clean(taskId);
      if (!cleanedTaskId) {
        setSopContext(null);
        setSopContextErr("");
        return null;
      }

      setSopContextLoading(true);
      setSopContextErr("");

      try {
        const payload = await requestJson(`/sops/task-context/${encodeURIComponent(cleanedTaskId)}`, {
          signal,
          params: {
            limit: 25,
          },
        });
        setSopContext(payload || null);
        return payload || null;
      } catch (error) {
        if (error?.name !== "AbortError") {
          const message = String(error?.message || error || "Task SOP context failed to load.");
          setSopContextErr(message);
          console.warn("Task SOP context failed to load", error);
        }
        return null;
      } finally {
        setSopContextLoading(false);
      }
    },
    [requestJson]
  );

  const loadTaskRemediationGuidanceContext = useCallback(
    async (taskId, signal) => {
      const cleanedTaskId = clean(taskId);
      if (!cleanedTaskId) {
        setRemediationGuidanceContext(null);
        setRemediationGuidanceErr("");
        return null;
      }

      setRemediationGuidanceLoading(true);
      setRemediationGuidanceErr("");

      try {
        const payload = await requestJson(`/remediation/tasks/${encodeURIComponent(cleanedTaskId)}/guidance`, {
          signal,
        });
        setRemediationGuidanceContext(payload || null);
        return payload || null;
      } catch (error) {
        if (error?.name !== "AbortError") {
          const message = String(error?.message || error || "Task remediation guidance failed to load.");
          setRemediationGuidanceErr(message);
          console.warn("Task remediation guidance failed to load", error);
        }
        return null;
      } finally {
        setRemediationGuidanceLoading(false);
      }
    },
    [requestJson]
  );

  const loadMissionExecution = useCallback(
    async ({ preferredWorkItemId = selectedId } = {}) => {
      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => {
        controller.abort("Mission Execution request timed out.");
      }, REQUEST_TIMEOUT_MS);

      try {
        setErr("");
        setLoading(true);

        const [analyticsPayload, queuePayload] = await Promise.all([
          requestJson("/execution/analytics", { signal: controller.signal }),
          requestJson("/execution/queue", { signal: controller.signal, params: { limit: 100 } }),
        ]);

        const queueItems = normalizeItems(queuePayload);
        setAnalytics(analyticsPayload?.metrics || analyticsPayload || null);
        setQueue(queueItems);

        const nextSelectedId = preferredWorkItemId || queueItems[0]?.work_item_id || queueItems[0]?.id || "";
        setSelectedId(nextSelectedId);

        if (nextSelectedId) {
          await loadWorkspace(nextSelectedId, controller.signal);
        } else {
          setWorkspace(null);
          setTimeline([]);
        }

        // Supplemental intelligence and planning surfaces must never gate the
        // core Mission Execution workbench. Load them independently after the
        // queue, analytics, and selected workspace are available.
        void Promise.allSettled([
          loadDecisionSupport(),
          loadWorkOptimizationContext(),
          loadExecutionPlanningSummary(),
          loadLearningSummary(),
        ]);
      } catch (error) {
        if (error?.name !== "AbortError") {
          setErr(String(error?.message || error || "Mission Execution failed to load."));
        }
      } finally {
        window.clearTimeout(timeoutId);
        setLoading(false);
      }
    },
    [
      loadDecisionSupport,
      loadExecutionPlanningSummary,
      loadLearningSummary,
      loadWorkOptimizationContext,
      loadWorkspace,
      requestJson,
      selectedId,
    ]
  );

  useEffect(() => {
    loadMissionExecution({ preferredWorkItemId: selectedId });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (loading || !tenantId || !userId) return undefined;

    const controller = new AbortController();

    loadPageIntelligence(
      "What should I work on first?",
      controller.signal
    );

    return () => controller.abort("mission-execution-page-intelligence-refresh");
  }, [
    frameworkFilter,
    loadPageIntelligence,
    loading,
    tenantId,
    userId,
  ]);

  useEffect(() => {
    return () => {
      if (taskLearningRefreshResetRef.current) {
        window.clearTimeout(taskLearningRefreshResetRef.current);
        taskLearningRefreshResetRef.current = null;
      }
      if (learningSummaryRefreshResetRef.current) {
        window.clearTimeout(learningSummaryRefreshResetRef.current);
        learningSummaryRefreshResetRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!actionMessage) return undefined;
    const timeoutId = window.setTimeout(() => setActionMessage(""), 5000);
    return () => window.clearTimeout(timeoutId);
  }, [actionMessage]);

  useEffect(() => {
    setBlockConfirmation("");
    setValidationRerunResult(null);
    setValidationRunHistory([]);
    setEvidenceContext(null);
    setEvidenceContextErr("");
    setWorkflowContext(null);
    setWorkflowContextErr("");
    setSopContext(null);
    setSopContextErr("");
    setRemediationGuidanceContext(null);
    setRemediationGuidanceErr("");
    setTaskOptimizationContext(null);
    setTaskOptimizationErr("");
    setExecutionPlanContext(null);
    setExecutionPlanErr("");
    setTaskLearningContext(null);
    setTaskLearningErr("");
    setTaskLearningRefreshState("idle");
    setTaskIntelligence(null);
    setTaskIntelligenceErr("");
    setTaskIntelligenceQuestion("");
    if (taskLearningRefreshResetRef.current) {
      window.clearTimeout(taskLearningRefreshResetRef.current);
      taskLearningRefreshResetRef.current = null;
    }
  }, [selectedId]);

  const selected = workspace?.item || workspace?.context?.selected || null;

  const selectedQueueItemForCopilot = useMemo(() => {
    const selectedTaskId = normalizeTaskId(selectedId);
    if (!selectedTaskId) return null;

    return (
      queue.find((item) => taskIdCandidates(item).includes(selectedTaskId)) ||
      null
    );
  }, [queue, selectedId]);

  const selectedCopilotTaskId = useMemo(() => {
    // Prefer the operator's current queue selection so changing tasks updates
    // Copilot immediately rather than waiting for the next workspace response.
    const fromSelectedId = remediationTaskIdForValidation(null, selectedId);
    if (fromSelectedId) return fromSelectedId;

    const fromQueue = remediationTaskIdForValidation(
      selectedQueueItemForCopilot,
      selectedId
    );
    if (fromQueue) return fromQueue;

    return remediationTaskIdForValidation(selected, selectedId);
  }, [selected, selectedId, selectedQueueItemForCopilot]);

  const selectedCopilotFocusLabel = useMemo(() => {
    const selectedTaskId = normalizeTaskId(selectedCopilotTaskId);
    const workspaceMatchesSelection = selectedTaskId
      ? taskIdCandidates(selected || {}).includes(selectedTaskId)
      : false;
    const focusSource =
      selectedQueueItemForCopilot ||
      (workspaceMatchesSelection ? selected : null);

    return clean(
      focusSource?.rule_id ||
        focusSource?.raw_task?.rule_id ||
        focusSource?.control_id ||
        focusSource?.title ||
        "Selected task"
    );
  }, [selected, selectedCopilotTaskId, selectedQueueItemForCopilot]);

  useEffect(() => {
    const pageContext = {
      page: "mission_execution",
      surface: "mission_execution",
      intelligence_mode: selectedCopilotTaskId
        ? "contextual_explain"
        : "page",
      ...(frameworkFilter && frameworkFilter !== "all"
        ? { framework: frameworkFilter }
        : {}),
      ...(activeFilter ? { filter: activeFilter, queue: activeFilter } : {}),
      ...(activeTab ? { tab: activeTab } : {}),
    };

    if (!selectedCopilotTaskId) {
      // Fail closed when Mission Execution has no exact task selected. Clear any
      // previous object selection, then retain only safe page-level UI intent.
      clearCopilotContext();
      setCopilotContext(pageContext);
      return;
    }

    // The browser supplies selection identity only. The backend resolves the
    // authoritative operational graph for the selected task.
    setCopilotContext({
      ...pageContext,
      record_type: "remediation_task",
      task_id: selectedCopilotTaskId,

      // Local display metadata only. AdvisorContext intentionally excludes these
      // fields from the governed backend request sanitizer.
      focus_label: selectedCopilotFocusLabel,
      focus_type: "Remediation task",
    });
  }, [
    activeFilter,
    activeTab,
    clearCopilotContext,
    frameworkFilter,
    selectedCopilotFocusLabel,
    selectedCopilotTaskId,
    setCopilotContext,
  ]);

  const decisionItems = useMemo(() => normalizeItems(decisionSupport?.decisions || decisionSupport), [decisionSupport]);
  const selectedDecision = useMemo(() => {
    if (!selected || !decisionItems.length) return decisionItems[0] || null;

    const selectedKeys = new Set(
      [
        selected.work_item_id,
        selected.id,
        selected.remediation_task_id,
        selected.task_id,
        selected.raw_task?.id,
      ]
        .map(clean)
        .filter(Boolean)
    );

    return (
      decisionItems.find((decision) => selectedKeys.has(decisionAffectedTaskId(decision))) ||
      decisionItems[0] ||
      null
    );
  }, [decisionItems, selected]);

  useEffect(() => {
    const taskId = remediationTaskIdForValidation(selected, selectedId);
    if (!taskId) {
      setValidationRunHistory([]);
      setEvidenceContext(null);
      setEvidenceContextErr("");
      setWorkflowContext(null);
      setWorkflowContextErr("");
      setSopContext(null);
      setSopContextErr("");
      setRemediationGuidanceContext(null);
      setRemediationGuidanceErr("");
      setTaskOptimizationContext(null);
      setTaskOptimizationErr("");
      setExecutionPlanContext(null);
      setExecutionPlanErr("");
      setTaskLearningContext(null);
      setTaskLearningErr("");
      setTaskLearningRefreshState("idle");
      setTaskIntelligence(null);
      setTaskIntelligenceErr("");
      setTaskIntelligenceQuestion("");
      return undefined;
    }

    const controller = new AbortController();
    loadValidationRunHistory(taskId, controller.signal);
    loadTaskEvidenceContext(taskId, controller.signal);
    loadTaskWorkflowContext(taskId, controller.signal);
    loadTaskSOPContext(taskId, controller.signal);
    loadTaskRemediationGuidanceContext(taskId, controller.signal);
    loadTaskOptimizationContext(taskId, controller.signal);
    loadExecutionPlanContext(taskId, controller.signal);
    loadTaskLearningContext(taskId, controller.signal);
    loadTaskIntelligence(taskId, "Why is OASYS telling me to do this first?", controller.signal);

    return () => controller.abort();
  }, [
    loadTaskEvidenceContext,
    loadExecutionPlanContext,
    loadTaskIntelligence,
    loadTaskLearningContext,
    loadTaskOptimizationContext,
    loadTaskRemediationGuidanceContext,
    loadTaskSOPContext,
    loadTaskWorkflowContext,
    loadValidationRunHistory,
    selected,
    selectedId,
  ]);
  const openTaskExecutionModal = useCallback((initialTab = "overview") => {
    if (!selected) return;
    setTaskExecutionModal({ open: true, initialTab });
  }, [selected]);

  const closeTaskExecutionModal = useCallback(() => {
    setTaskExecutionModal((current) => ({ ...current, open: false }));
  }, []);

  const context = workspace?.context || {};
  const panels = context?.panels || {};
  const queueHealth = useMemo(() => analytics?.queue_health || {}, [analytics]);
  const groupMetrics = useMemo(() => analytics?.groups || {}, [analytics]);

  const sortedTimeline = useMemo(() => {
    return [...timeline].sort((left, right) => {
      const leftTime = new Date(left?.occurred_at || left?.created_at || 0).getTime();
      const rightTime = new Date(right?.occurred_at || right?.created_at || 0).getTime();
      const safeLeft = Number.isFinite(leftTime) ? leftTime : 0;
      const safeRight = Number.isFinite(rightTime) ? rightTime : 0;
      return safeRight - safeLeft;
    });
  }, [timeline]);

  const frameworkOptions = useMemo(() => {
    const values = Array.from(new Set(queue.map((item) => clean(item.framework)).filter(Boolean)));
    return values.sort();
  }, [queue]);

  const optimizationLookup = useMemo(() => {
    const lookup = new Map();
    const addCategory = (items, category) => {
      optimizationItems(items).forEach((item) => {
        const taskId = normalizeTaskId(item.task_id || item.id);
        if (!taskId) return;
        const existing = lookup.get(taskId) || { categories: new Set(), items: {} };
        existing.categories.add(category);
        existing.items[category] = item;
        lookup.set(taskId, existing);
      });
    };

    addCategory(workOptimizationContext?.quick_wins, "quick_wins");
    addCategory(workOptimizationContext?.fast_follows, "fast_follows");
    addCategory(workOptimizationContext?.high_impact, "high_impact");

    optimizationItems(workOptimizationContext?.batches).forEach((batch) => {
      optimizationItems(batch.task_ids).forEach((taskIdValue) => {
        const taskId = normalizeTaskId(taskIdValue);
        if (!taskId) return;
        const existing = lookup.get(taskId) || { categories: new Set(), items: {} };
        existing.categories.add("batch_opportunities");
        existing.items.batch_opportunities = batch;
        lookup.set(taskId, existing);
      });
    });

    return lookup;
  }, [workOptimizationContext]);

  const selectedOptimizationMatch = useMemo(() => {
    const taskId = remediationTaskIdForValidation(selected, selectedId);
    return taskId ? optimizationLookup.get(taskId) || null : null;
  }, [optimizationLookup, selected, selectedId]);

  const selectedOptimizationPanelContext = useMemo(() => {
    const taskContext = taskOptimizationContext && typeof taskOptimizationContext === "object" ? taskOptimizationContext : {};
    const match = selectedOptimizationMatch || {};
    return {
      ...taskContext,
      quick_wins: taskContext.quick_wins || (match.items?.quick_wins ? [match.items.quick_wins] : []),
      fast_follows: taskContext.fast_follows || (match.items?.fast_follows ? [match.items.fast_follows] : []),
      high_impact: taskContext.high_impact || (match.items?.high_impact ? [match.items.high_impact] : []),
      batches: taskContext.batches || (match.items?.batch_opportunities ? [match.items.batch_opportunities] : []),
    };
  }, [selectedOptimizationMatch, taskOptimizationContext]);

  const filteredQueue = useMemo(() => {
    const q = clean(query).toLowerCase();
    const rows = queue.filter((item) => {
      const group = clean(item.queue_group).toLowerCase();
      const framework = clean(item.framework);
      const status = clean(item.status).toLowerCase();
      const validationStatus = clean(item.validation_status).toLowerCase();
      const owner = clean(item.owner_name || item.owner_id).toLowerCase();
      const operator = clean(userName || userId).toLowerCase();

      const optimizationMatch = optimizationCategoryForItem(item, optimizationLookup);
      const optimizationCategories = optimizationMatch?.categories || new Set();

      let matchesFilter = true;
      if (activeFilter === "ai_priority") {
        matchesFilter = status !== "closed" && status !== "cancelled";
      } else if (["quick_wins", "fast_follows", "high_impact", "batch_opportunities"].includes(activeFilter)) {
        matchesFilter = optimizationCategories.has(activeFilter);
      } else if (activeFilter === "validation_ready") {
        matchesFilter = validationStatus === "ready_for_validation" || validationStatus === "pending" || group === "waiting_validation" || status === "ready_for_validation";
      } else if (activeFilter === "my_work") {
        matchesFilter = !owner || owner === operator || owner.includes(operator) || status !== "closed";
      } else if (activeFilter === "ready_to_work") {
        matchesFilter = ["ready_to_work", "high_priority", "overdue"].includes(group) && status !== "blocked";
      } else if (activeFilter === "high_priority") {
        matchesFilter = group === "high_priority" || String(item.priority_label || "").toLowerCase() === "high";
      } else {
        matchesFilter = group === activeFilter || validationStatus === activeFilter;
      }

      const matchesFramework = frameworkFilter === "all" || framework === frameworkFilter;
      const haystack = [
        item.title,
        item.description,
        item.control_id,
        item.rule_id,
        item.resource_id,
        item.connector,
        item.connector_id,
        item.owner_name,
        item.framework,
        item.validation_status,
        item.workflow_status,
      ]
        .map((value) => clean(value).toLowerCase())
        .join(" ");
      const matchesQuery = !q || haystack.includes(q);
      return matchesFilter && matchesFramework && matchesQuery;
    });

    if (activeFilter === "ai_priority") {
      return rows.sort((left, right) => {
        const leftMatch = optimizationCategoryForItem(left, optimizationLookup);
        const rightMatch = optimizationCategoryForItem(right, optimizationLookup);
        const leftScore = Number(
          leftMatch?.items?.quick_wins?.optimization_score ||
            leftMatch?.items?.fast_follows?.optimization_score ||
            leftMatch?.items?.high_impact?.optimization_score ||
            0
        );
        const rightScore = Number(
          rightMatch?.items?.quick_wins?.optimization_score ||
            rightMatch?.items?.fast_follows?.optimization_score ||
            rightMatch?.items?.high_impact?.optimization_score ||
            0
        );
        return (Number.isFinite(rightScore) ? rightScore : 0) - (Number.isFinite(leftScore) ? leftScore : 0);
      });
    }

    return rows;
  }, [activeFilter, frameworkFilter, optimizationLookup, query, queue, userId, userName]);

  const operationalSummary = useMemo(() => {
    const assigned = queue.filter((item) => {
      const status = clean(item.status).toLowerCase();
      return !["closed", "cancelled", "completed"].includes(status);
    }).length;

    const readyNow = queue.filter((item) => {
      const group = clean(item.queue_group).toLowerCase();
      const status = clean(item.status).toLowerCase();
      return ["overdue", "high_priority", "ready_to_work"].includes(group) && status !== "blocked";
    }).length;

    return {
      assigned: queueHealth.active ?? assigned,
      readyNow,
      waitingValidation: groupMetrics.waiting_validation ?? queue.filter((item) => clean(item.queue_group).toLowerCase() === "waiting_validation").length,
      blocked: queueHealth.blocked ?? groupMetrics.blocked ?? 0,
      completedToday: analytics?.tasks_completed_today ?? 0,
    };
  }, [analytics, groupMetrics, queue, queueHealth]);

  const validationSummary = selected?.validation_summary || {};
  const latestLink = validationSummary?.latest?.link || {};
  const evidenceSummary = selected?.evidence_summary || {};
  const evidenceItems = useMemo(() => {
    const items = selected?.evidence_summary?.items;
    return Array.isArray(items) ? items : [];
  }, [selected]);
  const requiredActions = firstArray(latestLink.required_actions, validationSummary.required_actions);
  const blockingReasons = firstArray(latestLink.blocking_reasons, validationSummary.blocking_reasons);
  const closureAllowed = validationSummary.closure_allowed === true;
  const validationStatus = clean(validationSummary.status || validationSummary.result || selected?.validation_status || selected?.status);
  const selectedQueueBucket = formatQueueGroup(selected?.queue_label || selected?.queue_group || "");
  const recommendationConfidence =
    selected?.recommendation_confidence ||
    selected?.confidence_score ||
    validationSummary?.confidence_score ||
    latestLink?.confidence_score ||
    null;

  const recommendedActionTitle = useMemo(() => {
    if (!selected) return "Select work to see the next action.";
    if (clean(selected.status).toLowerCase() === "blocked") return "Resolve the blocker before continuing.";
    if (closureAllowed) return "Validation passed. Prepare this item for closure.";
    if (blockingReasons.length) return "Fix the validation blocker first.";
    if (clean(selected.status).toLowerCase() === "ready_for_validation") return "Run validation and confirm evidence.";
    return "Start work and follow the remediation guidance.";
  }, [blockingReasons.length, closureAllowed, selected]);

  const recommendedActionDetail = useMemo(() => {
    if (!selected) return "Use Work My Queue or select a work item from the operational queue.";
    if (blockingReasons.length) return blockingReasons[0];
    if (requiredActions.length) return requiredActions[0];
    return selected.description || "Review the task context, complete the remediation, then validate evidence.";
  }, [blockingReasons, requiredActions, selected]);

  const workflowSummary = selected?.workflow_summary || {};
  const workflowSummaryKeys = Object.keys(workflowSummary);
  const workflowStatus = clean(
    workflowSummary.status ||
      workflowSummary.workflow_status ||
      workflowSummary.state ||
      selected?.workflow_status ||
      "not_linked"
  );
  const workflowLinked =
    workflowSummaryKeys.length > 0 &&
    !["not_linked", "not linked", "none", "null"].includes(workflowStatus.toLowerCase());

  const workflowDependencies = firstArray(
    workflowSummary.dependencies,
    workflowSummary.dependency_items,
    workflowSummary.blocking_dependencies,
    workflowSummary.required_dependencies,
    workflowSummary.dependency_summary?.items
  );
  const workflowApprovals = firstArray(
    workflowSummary.approvals,
    workflowSummary.approval_items,
    workflowSummary.pending_approvals,
    workflowSummary.required_approvals,
    workflowSummary.approval_summary?.items
  );
  const workflowRecovery = firstArray(
    workflowSummary.recovery_actions,
    workflowSummary.recovery_steps,
    workflowSummary.recovery?.items,
    workflowSummary.recovery_plan
  );
  const workflowEvents = sortedTimeline
    .filter((event) => clean(event?.event_type).toLowerCase().includes("workflow"))
    .slice(0, 3);

  const workflowCurrentStep =
    clean(workflowSummary.current_step) ||
    clean(workflowSummary.current_step_name) ||
    clean(workflowSummary.step_name) ||
    clean(workflowSummary.step) ||
    clean(workflowSummary.current_state) ||
    (workflowLinked ? titleCase(workflowStatus) : "Manual execution");

  const workflowNextStep = workflowLinked
    ? clean(workflowSummary.next_step) ||
      clean(workflowSummary.recommended_next_step) ||
      clean(workflowSummary.next_action) ||
      "Continue the linked workflow or use the action buttons in the active workspace."
    : "Open Task, complete remediation steps, then run validation.";


  const relatedSop =
    panels?.right?.related_sop ||
    panels?.sop ||
    context?.sop ||
    selected?.sop_summary ||
    selected?.related_sop ||
    selected?.raw_task?.related_sop ||
    {};
  const linkedSopId = clean(
    relatedSop.linked_sop_id ||
      relatedSop.sop_id ||
      relatedSop.id ||
      selected?.linked_sop_id ||
      selected?.raw_task?.linked_sop_id
  );
  const sopTitle =
    clean(relatedSop.title) ||
    clean(relatedSop.name) ||
    clean(relatedSop.sop_title) ||
    clean(relatedSop.document_title) ||
    linkedSopId ||
    `${selected?.framework || "Framework"} remediation guidance`;
  const sopStatus = clean(relatedSop.status || relatedSop.state || (linkedSopId ? "linked" : "not_linked"));
  const sopLinked =
    Boolean(linkedSopId) ||
    ["linked", "active", "published", "available", "attached"].includes(sopStatus.toLowerCase());
  const sopSteps = firstArray(
    relatedSop.steps,
    relatedSop.remediation_steps,
    relatedSop.guidance_steps,
    relatedSop.procedure_steps,
    relatedSop.sections?.steps,
    selected?.raw_task?.sop_steps
  );
  const sopRelatedAlerts = panels?.right?.related_alerts || selected?.related_alerts || {};
  const sopRelatedAlertCount = Array.isArray(sopRelatedAlerts)
    ? sopRelatedAlerts.length
    : sopRelatedAlerts && typeof sopRelatedAlerts === "object"
      ? Object.keys(sopRelatedAlerts).length
      : 0;
  const sopFallbackSteps = requiredActions.length
    ? requiredActions.slice(0, 4)
    : [
        "Review the active work summary and remediation guidance.",
        "Complete the remediation steps required for this finding.",
        "Capture or refresh supporting evidence.",
        "Run validation before preparing the work item for closure.",
      ];

  const progressCurrentIndex = useMemo(() => {
    const status = clean(selected?.status).toLowerCase();
    const workflowStatus = clean(selected?.workflow_status).toLowerCase();
    const validation = clean(validationStatus).toLowerCase();
    if (["closed", "completed"].includes(status)) return 6;
    if (["passed", "approved_exception", "manual_override"].includes(validation)) return 6;
    if (["ready_for_validation", "failed", "stale", "pending"].includes(validation) || status === "ready_for_validation") return 5;
    if (workflowStatus && workflowStatus !== "not_linked") return 4;
    if (["in_progress", "blocked"].includes(status)) return 3;
    if (selected?.recommendation_handoff_id || selected?.recommendation_summary) return 2;
    return 3;
  }, [selected, validationStatus]);

  const operatorNotes = useMemo(() => {
    return sortedTimeline
      .filter((event) => clean(event?.event_type).toLowerCase() === "operator_note_added")
      .slice(0, 2);
  }, [sortedTimeline]);

  const lastOperatorHistoryEvent = sortedTimeline.find((event) => {
    const eventType = clean(event?.event_type).toLowerCase();
    return eventType.includes("operator") || eventType.includes("note");
  }) || null;
  const lastValidationHistoryEvent = sortedTimeline.find((event) => clean(event?.event_type).toLowerCase().includes("validation")) || null;
  const lastWorkflowHistoryEvent = sortedTimeline.find((event) => clean(event?.event_type).toLowerCase().includes("workflow")) || null;

  const validationHistoryEventsFromRuns = useMemo(() => {
    return (Array.isArray(validationRunHistory) ? validationRunHistory : [])
      .map((run) => {
        const validationRunId = clean(run?.validation_run_id || run?.id);
        const result = clean(run?.result_summary || run?.result || run?.status);
        const reason = clean(run?.reason || run?.summary || run?.metadata?.reason);
        const occurredAt =
          clean(run?.completed_at || run?.finished_at || run?.updated_at || run?.created_at || run?.started_at) ||
          "Time not recorded";

        return {
          id: validationRunId || clean(run?.id),
          event_type: "validation_run",
          title: result ? `Validation ${titleCase(result)}` : "Validation Run",
          message: reason || clean(run?.summary) || "Validation run recorded from the validation dashboard history.",
          actor_name: clean(run?.triggered_by_name) || clean(run?.triggered_by_user_id) || "OASYS",
          occurred_at: occurredAt,
          metadata: {
            validation_run_id: validationRunId,
            validation_result_ids: run?.validation_result_ids,
            confidence_score: run?.confidence_score,
            confidence_level: run?.confidence_level,
            source: "validation_runs_endpoint",
          },
        };
      })
      .filter((event) => clean(event.title) || clean(event.message));
  }, [validationRunHistory]);

  const modalTask = useMemo(() => {
    if (!selected) return selected;

    const summary = validationRerunResult?.summary || validationRerunResult || null;
    if (!summary || typeof summary !== "object") return selected;

    return {
      ...selected,
      validation_rerun_result: summary,
      validation_summary: {
        ...(selected.validation_summary || {}),
        last_rerun: summary,
        closure_guard: summary.closure_guard || selected.validation_summary?.closure_guard,
        status: summary.status || selected.validation_summary?.status || selected.validation_status,
        result: summary.result || selected.validation_summary?.result,
        confidence_score: summary.confidence_score || selected.validation_summary?.confidence_score,
        confidence_level: summary.confidence_level || selected.validation_summary?.confidence_level,
        blocker_reason: summary.reason || selected.validation_summary?.blocker_reason,
        reason: summary.reason || selected.validation_summary?.reason,
      },
    };
  }, [selected, validationRerunResult]);

  const validationModalEvents = useMemo(() => {
    const timelineEvents = sortedTimeline.filter((event) =>
      clean(event?.event_type || event?.type || event?.category).toLowerCase().includes("validation")
    );

    const summary = validationRerunResult?.summary || validationRerunResult || null;
    const latestRerunEvents = [];

    if (summary && typeof summary === "object") {
      const result = clean(summary.result || summary.status);
      const reason = clean(summary.reason);
      const validationRunId = clean(summary.validation_run_id);
      const occurredAt = clean(summary.completed_at || summary.updated_at || summary.created_at) || new Date().toISOString();

      latestRerunEvents.push({
        id: validationRunId || `validation-rerun-${occurredAt}`,
        event_type: "validation_rerun_result",
        title: result ? `Validation ${titleCase(result)}` : "Validation Rerun Completed",
        message: reason || "Deterministic validation rerun completed.",
        actor_name: userName,
        occurred_at: occurredAt,
        metadata: {
          validation_run_id: validationRunId,
          validation_result_ids: summary.validation_result_ids,
          confidence_score: summary.confidence_score,
          confidence_level: summary.confidence_level,
          closure_guard: summary.closure_guard,
          source: "mission_execution_validation_modal",
        },
      });
    }

    const seen = new Set();
    return [...latestRerunEvents, ...validationHistoryEventsFromRuns, ...timelineEvents]
      .filter((event) => {
        const key =
          clean(event?.metadata?.validation_run_id) ||
          clean(event?.validation_run_id) ||
          clean(event?.id || event?.event_id) ||
          `${clean(event?.event_type || event?.type)}::${clean(event?.title)}::${clean(event?.occurred_at || event?.created_at)}`;
        if (!key) return true;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .sort((left, right) => {
        const leftTime = new Date(left?.occurred_at || left?.created_at || 0).getTime();
        const rightTime = new Date(right?.occurred_at || right?.created_at || 0).getTime();
        const safeLeft = Number.isFinite(leftTime) ? leftTime : 0;
        const safeRight = Number.isFinite(rightTime) ? rightTime : 0;
        return safeRight - safeLeft;
      });
  }, [sortedTimeline, userName, validationHistoryEventsFromRuns, validationRerunResult]);


  const evidenceModalRecords = useMemo(() => {
    const fromTaskContext = normalizeItems(
      evidenceContext?.records ||
        evidenceContext?.items ||
        evidenceContext?.evidence_records ||
        evidenceContext?.evidence
    );

    const fromWorkspaceSummary = normalizeItems(evidenceItems);
    const seen = new Set();

    return [...fromTaskContext, ...fromWorkspaceSummary].filter((record, index) => {
      const key =
        clean(record?.id) ||
        clean(record?.evidence_id) ||
        clean(record?.validation_evidence_link_id) ||
        clean(record?.validation_result_id) ||
        clean(record?.validation_run_id) ||
        `${clean(record?.source || record?.evidence_source)}::${clean(record?.created_at || record?.updated_at)}::${index}`;
      if (!key) return true;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [evidenceContext, evidenceItems]);

  const scrollToWorkbench = useCallback(() => {
    window.requestAnimationFrame(() => {
      const target = activeWorkRef.current || workbenchRef.current;
      target?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, []);

  const openWorkItem = useCallback(
    async (workItemId, options = {}) => {
      if (!workItemId) return;

      const shouldScroll = options?.scroll !== false;
      setSelectedId(workItemId);
      setErr("");

      try {
        await loadWorkspace(workItemId);
        if (shouldScroll) scrollToWorkbench();
      } catch (error) {
        setErr(String(error?.message || error || "Work item failed to load."));
      }
    },
    [loadWorkspace, scrollToWorkbench]
  );

  useEffect(() => {
    if (loading || !filteredQueue.length) return;

    const firstItem = filteredQueue[0];
    const firstItemId = firstItem?.work_item_id || firstItem?.id || firstItem?.task_id || "";
    if (!firstItemId) return;

    const filterSignature = [activeFilter, frameworkFilter, clean(query).toLowerCase()].join("::");
    const filterChanged = lastAutoSelectionRef.current !== filterSignature;
    const selectedTaskId = normalizeTaskId(selectedId);
    const selectedIsVisible = selectedTaskId
      ? filteredQueue.some((item) => taskIdCandidates(item).includes(selectedTaskId))
      : false;

    if (!filterChanged && selectedIsVisible) return;

    lastAutoSelectionRef.current = filterSignature;
    if (normalizeTaskId(firstItemId) === selectedTaskId) return;

    openWorkItem(firstItemId, { scroll: false });
  }, [activeFilter, filteredQueue, frameworkFilter, loading, openWorkItem, query, selectedId]);
  const openDecisionWorkbench = useCallback(async (decision = selectedDecision) => {
    const affectedTaskId = decisionAffectedTaskId(decision);
    if (affectedTaskId && affectedTaskId !== selectedId) {
      await openWorkItem(affectedTaskId);
    }
    setTaskExecutionModal({ open: true, initialTab: "decision" });
  }, [openWorkItem, selectedDecision, selectedId]);



  const refreshOperationalState = useCallback(
    async (workItemId) => {
      const [analyticsPayload, queuePayload] = await Promise.all([
        requestJson("/execution/analytics"),
        requestJson("/execution/queue", { params: { limit: 100 } }),
      ]);

      setAnalytics(analyticsPayload?.metrics || analyticsPayload || null);
      setQueue(normalizeItems(queuePayload));

      if (workItemId) {
        await loadWorkspace(workItemId);
      }
    },
    [loadWorkspace, requestJson]
  );

  const workMyQueue = useCallback(async () => {
    setWorkingQueue(true);
    setErr("");
    try {
      const payload = await requestJson("/execution/work-my-queue", { method: "POST" });
      const nextItem = payload?.selected;
      const nextId = nextItem?.work_item_id || nextItem?.id || "";
      if (nextId) {
        setSelectedId(nextId);
        await loadWorkspace(nextId);
      }
      await loadMissionExecution({ preferredWorkItemId: nextId });
      scrollToWorkbench();
    } catch (error) {
      setErr(String(error?.message || error || "Work My Queue failed."));
    } finally {
      setWorkingQueue(false);
    }
  }, [loadMissionExecution, loadWorkspace, requestJson, scrollToWorkbench]);

  const runWorkItemAction = useCallback(
    async (action, endpoint, note = "") => {
      if (!selectedId || actionLoading) return;

      setActionLoading(action);
      setActionMessage("");
      setErr("");

      try {
        const payload = await requestJson(`/execution/work-item/${encodeURIComponent(selectedId)}/${endpoint}`, {
          method: "POST",
          body: note ? { note } : {},
        });

        if (payload?.item) {
          setWorkspace((current) => ({ ...(current || {}), item: payload.item }));
        }

        if (Array.isArray(payload?.timeline)) {
          setTimeline(payload.timeline);
        }

        setActionMessage(payload?.message || "Mission Execution action recorded.");
        await refreshOperationalState(selectedId);
      } catch (error) {
        setErr(String(error?.message || error || "Mission Execution action failed."));
      } finally {
        setActionLoading("");
        setBlockConfirmation("");
      }
    },
    [actionLoading, refreshOperationalState, requestJson, selectedId]
  );

  const runValidationRerun = useCallback(async () => {
    const taskId = remediationTaskIdForValidation(selected, selectedId);
    if (!taskId) {
      setErr("Unable to identify the remediation task for validation rerun.");
      return;
    }

    if (actionLoading) return;

    setActionLoading("validation_rerun");
    setActionMessage("");
    setErr("");

    try {
      const payload = await requestJson(`/validation/tasks/${encodeURIComponent(taskId)}/rerun`, {
        method: "POST",
      });

      setValidationRerunResult(payload || null);

      const summary = payload?.summary || {};
      const result = clean(summary.result || summary.status);
      const reason = clean(summary.reason);

      setActionMessage(
        result
          ? `Validation rerun complete: ${titleCase(result)}.${reason ? ` ${reason}` : ""}`
          : "Validation rerun completed and task context refreshed."
      );

      await refreshOperationalState(selectedId);
      await loadValidationRunHistory(taskId);
      await loadTaskEvidenceContext(taskId);
      await loadTaskWorkflowContext(taskId);
      await loadTaskSOPContext(taskId);
      await loadDecisionSupport(undefined);
    } catch (error) {
      setErr(String(error?.message || error || "Validation rerun failed."));
    } finally {
      setActionLoading("");
    }
  }, [actionLoading, loadDecisionSupport, loadTaskEvidenceContext, loadTaskSOPContext, loadTaskWorkflowContext, loadValidationRunHistory, refreshOperationalState, requestJson, selected, selectedId]);

  const addOperatorNote = useCallback(async () => {
    const note = clean(operatorNote);
    if (!note) {
      setErr("Add a note before recording an operator note.");
      return;
    }

    await runWorkItemAction("note", "note", note);
    setOperatorNote("");
  }, [operatorNote, runWorkItemAction]);

  const refreshDecisionSupport = useCallback(async () => {
    await loadDecisionSupport(undefined);
  }, [loadDecisionSupport]);

  const openDecisionDetail = useCallback(
    async (decision) => {
      const decisionId = clean(decision?.decision_id);
      if (!decisionId) return;

      setDecisionActionLoading(`${decisionId}:detail`);
      setDecisionSupportErr("");

      try {
        const detail = await requestJson(`/decision-support/${encodeURIComponent(decisionId)}`);
        setDecisionDetail(detail || null);
      } catch (error) {
        setDecisionSupportErr(String(error?.message || error || "Decision detail failed to load."));
      } finally {
        setDecisionActionLoading("");
      }
    },
    [requestJson]
  );

  const recordDecisionSupportFeedback = useCallback(
    async (decision, feedbackType) => {
      const decisionId = clean(decision?.decision_id);
      if (!decisionId || decisionActionLoading) return;

      setDecisionActionLoading(`${decisionId}:${feedbackType}`);
      setDecisionSupportErr("");

      try {
        const payload = await requestJson(`/decision-support/${encodeURIComponent(decisionId)}/feedback`, {
          method: "POST",
          body: {
            feedback_type: feedbackType,
            feedback_value: feedbackType === "accepted" ? "operator_accepts_recommendation" : "operator_feedback",
            comment: `Mission Execution operator marked this Decision Support recommendation as ${feedbackType}.`,
            metadata: {
              source: "mission_execution_center",
              surface: "mission_execution",
              affected_task: decisionAffectedTaskId(decision),
            },
          },
        });

        setActionMessage(payload?.result?.persistence_status === "persisted"
          ? "Decision Support feedback persisted."
          : "Decision Support feedback recorded.");

        await loadDecisionSupport(undefined);
        const detail = await requestJson(`/decision-support/${encodeURIComponent(decisionId)}`);
        setDecisionDetail(detail || null);
      } catch (error) {
        setDecisionSupportErr(String(error?.message || error || "Decision Support feedback failed."));
      } finally {
        setDecisionActionLoading("");
      }
    },
    [decisionActionLoading, loadDecisionSupport, requestJson]
  );

  const selectTab = useCallback((tab) => {
    setActiveTab(tab);
  }, []);

  if (loading) {
    return (
      <div className="mission-execution-page mec-loading-state" data-theme-mode={themeMode}>
        <div className="mec-loading-shell">
          <h1 className="mec-loading-title">Mission Execution</h1>
          <div className="mec-loading-card">Loading Mission Execution…</div>
        </div>
      </div>
    );
  }

  return (
    <div className="mission-execution-page" data-theme-mode={themeMode}>
      <section className="mec-title-row">
        <div>
          <h1>Mission Execution</h1>
          <p>Execute prioritized compliance work from a single operational workspace.</p>
        </div>
        <div className="mec-page-actions">
          <button
            type="button"
            className="mec-work-my-queue-button mec-work-my-queue-button--header"
            onClick={workMyQueue}
            disabled={workingQueue}
          >
            {workingQueue ? "Opening highest-value work…" : "Work My Queue"}
          </button>
        </div>
      </section>

      {err ? <div className="mec-error">{err}</div> : null}
      {actionMessage ? <div className="mec-action-feedback">{actionMessage}</div> : null}

      <MissionExecutionIntelligencePanel
        question={pageIntelligence.question}
        card={pageIntelligence.card}
        metadata={pageIntelligence.metadata}
        loading={pageIntelligence.loading}
        error={pageIntelligence.error}
        onAsk={loadPageIntelligence}
      />

      <section className="mec-execution-summary-row" aria-label="Execution summary">
        <MetricCard label="My Work" value={operationalSummary.assigned} footnote="Active Work" />
        <MetricCard label="Ready Now" value={operationalSummary.readyNow} footnote="Ready" tone="ok" />
        <MetricCard label="Waiting Validation" value={operationalSummary.waitingValidation} footnote="Validation" />
        <MetricCard label="Blocked" value={operationalSummary.blocked} footnote="Blocked" tone={operationalSummary.blocked ? "danger" : "ok"} />
        <MetricCard label="Completed Today" value={operationalSummary.completedToday} footnote="Completed" />
      </section>

      <div className="mec-execution-guidance" role="note">
        <strong>Operator controlled</strong>
        <span>OASYS prioritizes and prepares work. Execution and state changes remain under operator control.</span>
      </div>

      <section className="mec-workbench" ref={workbenchRef} aria-label="Mission Execution workbench">
        <aside className="mec-operational-queue" aria-label="Operational Work Queue">
          <div className="mec-column-header">
            <div>
              <h2>Operational Work Queue</h2>
              <p>{filteredQueue.length} visible work items</p>
            </div>
            <button type="button" className="mec-link-button" onClick={() => loadMissionExecution({ preferredWorkItemId: selectedId })}>
              Refresh
            </button>
          </div>

          <div className="mec-queue-filters" aria-label="Queue filters">
            {FILTERS.map((filter) => (
              <button
                type="button"
                key={filter.key}
                className={activeFilter === filter.key ? "active" : ""}
                onClick={() => setActiveFilter(filter.key)}
              >
                {filter.label}
              </button>
            ))}
          </div>

          <div className="mec-queue-search-row">
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search work…"
              aria-label="Search execution queue"
            />
            <select value={frameworkFilter} onChange={(event) => setFrameworkFilter(event.target.value)} aria-label="Filter by framework">
              <option value="all">All frameworks</option>
              {frameworkOptions.map((framework) => (
                <option key={framework} value={framework}>{framework}</option>
              ))}
            </select>
          </div>

          <div className="mec-queue-list">
            {filteredQueue.map((item) => {
              const itemId = item.work_item_id || item.id || item.task_id || "";
              const itemCandidateIds = taskIdCandidates(item);
              const selectedTaskId = normalizeTaskId(selectedId);
              const isActive = itemCandidateIds.includes(selectedTaskId);
              const itemOptimization = optimizationCategoryForItem(item, optimizationLookup);
              return (
                <button
                  type="button"
                  key={itemId || itemCandidateIds.join("-")}
                  className={`mec-queue-card ${isActive ? "active" : ""}`}
                  aria-current={isActive ? "true" : undefined}
                  onClick={() => openWorkItem(itemId)}
                >
                  <div className="mec-queue-card-top">
                    <StatusPill tone={String(item.priority_label || "").toLowerCase() === "high" ? "danger" : "neutral"}>
                      {humanizeDisplayValue(item.priority_label || item.priority, "Priority")}
                    </StatusPill>
                    <div className="mec-queue-card-meta">
                      {itemOptimization?.items?.quick_wins ? (
                        <EstimatedEffortBadge effort={itemOptimization.items.quick_wins.estimated_effort} compact />
                      ) : itemOptimization?.items?.fast_follows ? (
                        <EstimatedEffortBadge effort={itemOptimization.items.fast_follows.estimated_effort} compact />
                      ) : itemOptimization?.items?.high_impact ? (
                        <EstimatedEffortBadge effort={itemOptimization.items.high_impact.estimated_effort} compact />
                      ) : null}
                      <span className="mec-queue-card-due">{formatDate(item.due_date)}</span>
                    </div>
                  </div>
                  <strong>{item.title || "Untitled work item"}</strong>
                  <p>{item.description || "No description available."}</p>
                  <div className="mec-queue-card-grid" aria-label="Queue item summary">
                    <span>{item.framework || "—"}</span>
                    <span>{humanizeDisplayValue(item.connector || item.connector_id || item.provider, "Connector")}</span>
                    <span>{item.rule_id || item.control_id || "Finding"}</span>
                    <span>{humanizeDisplayValue(item.validation_status || item.status)}</span>
                  </div>
                </button>
              );
            })}
            {!filteredQueue.length ? (
              <EmptyState title="No visible work" message="Change filters or refresh the operational queue." />
            ) : null}
          </div>
        </aside>

        <main className="mec-active-work" ref={activeWorkRef} aria-label="Active Work Workspace">
          {selected ? (
            <>
              <div className="mec-selected-work-header">
                <div>
                  <p>{selectedQueueBucket || "Active Work"}</p>
                  <h2>{selected.title || "Selected work item"}</h2>
                  <span>{selected.description || "Review the selected remediation task and execute the recommended next action."}</span>
                </div>
                <StatusPill tone={String(selected.priority_label || "").toLowerCase() === "high" ? "danger" : "neutral"}>
                  {humanizeDisplayValue(selected.priority_label || selected.priority, "Priority")}
                </StatusPill>
              </div>

              <section className="mec-problem-card">
                <div>
                  <span>Problem Summary</span>
                  <h3>{selected.rule_id || selected.control_id || "Compliance finding"}</h3>
                  <p>{selected.description || `${selected.framework || "Framework"} ${selected.control_id || "control"} currently requires remediation.`}</p>
                </div>
                <div className="mec-problem-facts">
                  <DetailRow label="Framework" value={selected.framework} />
                  <DetailRow label="Connector" value={humanizeDisplayValue(selected.connector || selected.provider || selected.connector_id)} />
                  <DetailRow label="Control" value={selected.control_id} />
                  <DetailRow label="Status" value={humanizeDisplayValue(selected.status)} />
                  <DetailRow label="Confidence" value={recommendationConfidence ? formatPercentish(recommendationConfidence) : "—"} />
                </div>
              </section>

            </>
          ) : (
            <EmptyState title="No active work selected" message="Choose a task from the Operational Work Queue." />
          )}
        </main>

        <aside className="mec-context-panel" aria-label="Context Panel">
          <div className="mec-context-tabs" role="tablist" aria-label="Work item context tabs">
            {CONTEXT_TABS.map((tab) => (
              <button
                type="button"
                key={tab.key}
                role="tab"
                aria-selected={activeTab === tab.key}
                className={activeTab === tab.key ? "active" : ""}
                onClick={() => selectTab(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="mec-tab-body">
            {!selected ? (
              <EmptyState title="No work item selected" message="Context appears after a work item is selected." />
            ) : null}

            {selected && activeTab === "evidence" ? (
              <div className="mec-tab-stack">
                <div className="mec-tab-summary">
                  <span>Evidence Status</span>
                  <strong>{titleCase(evidenceSummary.status || (evidenceSummary.evidence_link_count ? "linked" : "not linked"))}</strong>
                  <p>{evidenceSummary.message || "Evidence context will appear when linked."}</p>
                </div>
                {evidenceItems.length ? evidenceItems.map((item, index) => (
                  <div className="mec-context-record" key={item.id || `evidence-${index}`}>
                    <strong>{humanizeDisplayValue(item.evidence_source, "Evidence Source")}</strong>
                    <p>{item.notes || "Supporting validation evidence."}</p>
                    <div>
                      <StatusPill>{humanizeDisplayValue(item.status)}</StatusPill>
                      <StatusPill>{humanizeDisplayValue(item.completeness_status)}</StatusPill>
                      <StatusPill tone={clean(item.freshness_status).toLowerCase() === "fresh" ? "ok" : "neutral"}>{humanizeDisplayValue(item.freshness_status)}</StatusPill>
                    </div>
                  </div>
                )) : <EmptyState title="No evidence linked" message="Evidence will appear here after validation or evidence linkage." />}
              </div>
            ) : null}

            {selected && activeTab === "workflow" ? (
              <div className="mec-tab-stack">
                <div className={`mec-workflow-state ${workflowLinked ? "is-linked" : "is-manual"}`}>
                  <div>
                    <span>Workflow Status</span>
                    <strong>{workflowLinked ? humanizeDisplayValue(workflowStatus) : "Not Linked"}</strong>
                    <p>
                      {workflowLinked
                        ? "A guided workflow is connected to this remediation task."
                        : "This remediation can still be worked manually. No guided workflow is currently attached."}
                    </p>
                  </div>
                  <StatusPill tone={workflowLinked ? "ok" : "neutral"}>
                    {workflowLinked ? "Workflow Linked" : "Manual Work"}
                  </StatusPill>
                </div>

                <div className="mec-workflow-next-step">
                  <span>Recommended Next Step</span>
                  <strong>{workflowNextStep}</strong>
                </div>

                <div className="mec-context-metrics mec-workflow-metric-grid">
                  <div>
                    <span>Current Step</span>
                    <strong>{workflowCurrentStep}</strong>
                    <small>{workflowLinked && workflowStatus.toLowerCase() === "queued" ? "awaiting workflow execution" : workflowLinked ? "linked workflow state" : "operator-driven"}</small>
                  </div>
                  <div>
                    <span>Dependencies</span>
                    <strong>{workflowCount(workflowDependencies)}</strong>
                    <small>{workflowDependencies.length ? "linked" : "none linked"}</small>
                  </div>
                  <div>
                    <span>Approvals</span>
                    <strong>{workflowCount(workflowApprovals)}</strong>
                    <small>{workflowApprovals.length ? "required" : "none linked"}</small>
                  </div>
                  <div>
                    <span>Recovery</span>
                    <strong>{workflowCount(workflowRecovery)}</strong>
                    <small>{workflowRecovery.length ? "available" : "not needed"}</small>
                  </div>
                </div>

                <div className="mec-workflow-section">
                  <span>Dependencies</span>
                  {workflowDependencies.length ? (
                    workflowDependencies.slice(0, 4).map((dependency, index) => (
                      <p key={`workflow-dependency-${index}`}>{summarizeWorkflowItem(dependency, "Linked dependency")}</p>
                    ))
                  ) : (
                    <p>No linked dependencies are currently blocking this work item.</p>
                  )}
                </div>

                <div className="mec-workflow-section">
                  <span>Approvals</span>
                  {workflowApprovals.length ? (
                    workflowApprovals.slice(0, 4).map((approval, index) => (
                      <p key={`workflow-approval-${index}`}>{summarizeWorkflowItem(approval, "Linked approval")}</p>
                    ))
                  ) : (
                    <p>No linked approvals are currently required inside Mission Execution.</p>
                  )}
                </div>

                <div className="mec-workflow-section">
                  <span>Last Workflow Event</span>
                  {workflowEvents.length ? (
                    workflowEvents.map((event, index) => (
                      <p key={`workflow-event-${index}`}>
                        {event.title || titleCase(event.event_type) || "Workflow event"} · {formatDateTime(event.occurred_at)}
                      </p>
                    ))
                  ) : (
                    <p>No workflow events are currently linked to this work item.</p>
                  )}
                </div>
              </div>
            ) : null}

            {selected && activeTab === "validation" ? (
              <div className="mec-tab-stack">
                <div className={`mec-validation-state ${closureAllowed ? "is-ready" : "is-blocked"}`}>
                  <div>
                    <span>Latest Validation</span>
                    <strong>{titleCase(validationStatus || "unknown")}</strong>
                    <p>{validationSummary.reason || validationSummary.blocker_reason || recommendedActionDetail}</p>
                  </div>
                  <StatusPill tone={closureAllowed ? "ok" : "danger"}>
                    {closureAllowed ? "Closure Ready" : "Closure Blocked"}
                  </StatusPill>
                </div>

                <div className="mec-context-metrics mec-validation-metric-grid">
                  <div>
                    <span>Confidence</span>
                    <strong>{titleCase(validationSummary.confidence_level || latestLink.confidence_level || "unknown")}</strong>
                    <small>{formatPercentish(validationSummary.confidence_score || latestLink.confidence_score)}</small>
                  </div>
                  <div>
                    <span>Links</span>
                    <strong>{validationSummary.counts?.remediation_validation_links ?? 0}</strong>
                    <small>task links</small>
                  </div>
                  <div>
                    <span>Evidence</span>
                    <strong>{validationSummary.counts?.evidence_links ?? 0}</strong>
                    <small>items</small>
                  </div>
                  <div>
                    <span>Runs</span>
                    <strong>{validationSummary.counts?.validation_runs ?? 0}</strong>
                    <small>validation runs</small>
                  </div>
                </div>

                <div className="mec-validation-section">
                  <span>Why Closure Is Blocked</span>
                  {blockingReasons.length ? (
                    blockingReasons.map((reason, index) => <p key={`blocker-${index}`}>{reason}</p>)
                  ) : closureAllowed ? (
                    <p>Validation does not currently report closure blockers.</p>
                  ) : (
                    <p>{recommendedActionDetail}</p>
                  )}
                </div>

                <div className="mec-validation-section">
                  <span>Required Actions</span>
                  {requiredActions.length ? (
                    <ol>
                      {requiredActions.slice(0, 5).map((action, index) => <li key={`validation-action-${index}`}>{action}</li>)}
                    </ol>
                  ) : (
                    <ol>
                      <li>Review the latest validation result.</li>
                      <li>Complete remediation work.</li>
                      <li>Re-run validation after evidence refresh.</li>
                    </ol>
                  )}
                </div>
              </div>
            ) : null}

            {selected && activeTab === "sop" ? (
              <div className="mec-tab-stack">
                <div className={`mec-sop-state ${sopLinked ? "is-linked" : "is-missing"}`}>
                  <div>
                    <span>SOP Status</span>
                    <strong>{sopLinked ? "Linked" : "Not Linked"}</strong>
                    <p>
                      {sopLinked
                        ? "Operating guidance is connected to this work item."
                        : "No formal SOP is currently linked. Use the task guidance and validation requirements below."}
                    </p>
                  </div>
                  <StatusPill tone={sopLinked ? "ok" : "neutral"}>
                    {sopLinked ? "SOP Linked" : "Manual Guidance"}
                  </StatusPill>
                </div>

                <div className="mec-sop-section">
                  <span>{sopLinked ? "Applicable SOP" : "Recommended Operating Guidance"}</span>
                  <strong>{sopTitle}</strong>
                  <p>
                    {sopLinked
                      ? "Follow this SOP while completing remediation and evidence validation."
                      : "Follow the task-specific remediation path until a formal SOP is attached."}
                  </p>
                </div>

                <div className="mec-context-metrics mec-sop-metric-grid">
                  <div>
                    <span>SOP ID</span>
                    <strong>{linkedSopId || "—"}</strong>
                    <small>{sopLinked ? "linked record" : "not linked"}</small>
                  </div>
                  <div>
                    <span>Version</span>
                    <strong>{valueOrDash(relatedSop.version || relatedSop.sop_version || relatedSop.revision)}</strong>
                    <small>SOP version</small>
                  </div>
                  <div>
                    <span>Owner</span>
                    <strong>{valueOrDash(relatedSop.owner || relatedSop.owner_name || relatedSop.team)}</strong>
                    <small>guidance owner</small>
                  </div>
                  <div>
                    <span>Related Alerts</span>
                    <strong>{sopRelatedAlertCount}</strong>
                    <small>alert context</small>
                  </div>
                </div>

                <div className="mec-sop-section">
                  <span>{sopLinked ? "Procedure Steps" : "Fallback Steps"}</span>
                  <ol>
                    {(sopSteps.length ? sopSteps : sopFallbackSteps).slice(0, 6).map((step, index) => (
                      <li key={`sop-step-${index}`}>{summarizeWorkflowItem(step, `Step ${index + 1}`)}</li>
                    ))}
                  </ol>
                </div>

                <div className="mec-sop-section">
                  <span>Control Mapping</span>
                  <div className="mec-sop-map">
                    <DetailRow label="Framework" value={selected.framework} />
                    <DetailRow label="Control" value={selected.control_id} />
                    <DetailRow label="Finding" value={selected.rule_id || selected.resource_id} />
                    <DetailRow label="Last Updated" value={formatDateTime(relatedSop.updated_at || relatedSop.last_updated_at || relatedSop.modified_at)} />
                  </div>
                </div>
              </div>
            ) : null}

            {selected && activeTab === "history" ? (
              <div className="mec-tab-stack">
                <div className="mec-history-summary-card">
                  <span>History Summary</span>
                  <strong>Timeline activity captured for this work item.</strong>
                </div>

                <div className="mec-context-metrics mec-history-metric-grid">
                  <div>
                    <span>Total Events</span>
                    <strong>{sortedTimeline.length}</strong>
                    <small>timeline records</small>
                  </div>
                  <div>
                    <span>Last Operator Action</span>
                    <strong>{lastOperatorHistoryEvent ? formatDateTime(lastOperatorHistoryEvent.occurred_at || lastOperatorHistoryEvent.created_at) : "—"}</strong>
                    <small>{lastOperatorHistoryEvent ? timelineActor(lastOperatorHistoryEvent) : "none recorded"}</small>
                  </div>
                  <div>
                    <span>Last Validation</span>
                    <strong>{lastValidationHistoryEvent ? formatDateTime(lastValidationHistoryEvent.occurred_at || lastValidationHistoryEvent.created_at) : "—"}</strong>
                    <small>{lastValidationHistoryEvent ? timelineTitle(lastValidationHistoryEvent) : "none recorded"}</small>
                  </div>
                  <div>
                    <span>Last Workflow</span>
                    <strong>{lastWorkflowHistoryEvent ? formatDateTime(lastWorkflowHistoryEvent.occurred_at || lastWorkflowHistoryEvent.created_at) : "—"}</strong>
                    <small>{lastWorkflowHistoryEvent ? timelineTitle(lastWorkflowHistoryEvent) : "none recorded"}</small>
                  </div>
                </div>

                <div className="mec-history-list" aria-label="Work item timeline">
                  {sortedTimeline.slice(0, 16).map((event, index) => {
                    const eventType = timelineEventType(event);
                    return (
                      <div className={`mec-history-event-card mec-history-event-${eventType.className}`} key={`${event.event_type || "event"}-${event.occurred_at || event.created_at || index}`}>
                        <div className="mec-history-event-top">
                          <StatusPill tone={eventType.tone}>{eventType.label}</StatusPill>
                          <span>{formatDateTime(event.occurred_at || event.created_at)}</span>
                        </div>
                        <strong>{timelineTitle(event)}</strong>
                        <p>{timelineMessage(event)}</p>
                        <div className="mec-history-event-meta" aria-label={`Actor: ${timelineActor(event)}`}>
                          <span className="mec-history-event-meta-label">Actor</span>
                          <strong className="mec-history-event-meta-value">{timelineActor(event)}</strong>
                        </div>
                      </div>
                    );
                  })}
                  {!sortedTimeline.length ? <EmptyState title="No history yet" message="Events appear as operators work this item." /> : null}
                </div>
              </div>
            ) : null}
          </div>
        </aside>

        <section className="mec-focused-work" aria-label="Focused task work">
          {selected ? (
            <>
              <OptimizationInsightPanel
                context={selectedOptimizationPanelContext}
                task={selected}
                loading={taskOptimizationLoading}
                error={taskOptimizationErr}
                compact
                onRefresh={() => loadTaskOptimizationContext(remediationTaskIdForValidation(selected, selectedId))}
                onOpenTask={openWorkItem}
              />

              <section className="mec-action-panel" aria-label="Task workbench actions">
                <div className="mec-action-panel-header">
                  <div>
                    <h3>Task Workbench Actions</h3>
                    <p>Open this task in a focused modal for guidance, workflow, validation, evidence, SOP, history, and Decision Support.</p>
                  </div>
                  <button type="button" className="mec-link-button" onClick={() => refreshOperationalState(selectedId)} disabled={Boolean(actionLoading)}>
                    Refresh Context
                  </button>
                </div>
                <div className="mec-action-buttons">
                  <button
                    type="button"
                    className="mec-primary-button"
                    onClick={() => openTaskExecutionModal("overview")}
                    disabled={Boolean(actionLoading)}
                  >
                    Review / Manage
                  </button>
                  <button
                    type="button"
                    className="mec-secondary-button"
                    onClick={() => openTaskExecutionModal("guidance")}
                    disabled={Boolean(actionLoading)}
                  >
                    Fix Plan
                  </button>
                  <button
                    type="button"
                    className="mec-secondary-button"
                    onClick={() => openTaskExecutionModal("execution_plan")}
                    disabled={Boolean(actionLoading)}
                  >
                    Execution Plan
                  </button>
                  <button
                    type="button"
                    className="mec-secondary-button"
                    onClick={() => openTaskExecutionModal("validation")}
                    disabled={Boolean(actionLoading)}
                  >
                    Run Validation
                  </button>
                  <button
                    type="button"
                    className="mec-secondary-button"
                    onClick={() => openTaskExecutionModal("evidence")}
                    disabled={Boolean(actionLoading)}
                  >
                    View Evidence
                  </button>
                  <button
                    type="button"
                    className="mec-secondary-button"
                    onClick={() => openTaskExecutionModal("workflow")}
                    disabled={Boolean(actionLoading)}
                  >
                    Open Workflow
                  </button>
                  <button
                    type="button"
                    className="mec-secondary-button"
                    onClick={() => openTaskExecutionModal("sop")}
                    disabled={Boolean(actionLoading)}
                  >
                    Open SOP
                  </button>
                  <button
                    type="button"
                    className="mec-secondary-button"
                    onClick={() => openTaskExecutionModal("history")}
                    disabled={Boolean(actionLoading)}
                  >
                    View History
                  </button>
                  <button
                    type="button"
                    className="mec-secondary-button"
                    onClick={() => openTaskExecutionModal("decision")}
                    disabled={Boolean(actionLoading)}
                  >
                    View Decision
                  </button>
                </div>
              </section>

              <section className="mec-execution-progress" aria-label="Execution progress">
                {PROGRESS_STEPS.map((step, index) => (
                  <div
                    key={step}
                    className={`mec-progress-step ${index < progressCurrentIndex ? "complete" : ""} ${index === progressCurrentIndex ? "current" : ""}`}
                  >
                    <span>{index + 1}</span>
                    <strong>{step}</strong>
                  </div>
                ))}
              </section>

              <DecisionSupportPanel
                loading={decisionSupportLoading}
                error={decisionSupportErr}
                decisions={decisionItems}
                selectedDecision={selectedDecision}
                selectedWorkItemId={
                  selected?.work_item_id ||
                  selected?.id ||
                  selected?.remediation_task_id ||
                  selected?.task_id ||
                  selected?.raw_task?.id ||
                  selectedId
                }
                detail={decisionDetail}
                onRefresh={refreshDecisionSupport}
                onOpenDetail={openDecisionDetail}
                onFeedback={recordDecisionSupportFeedback}
                onOpenDecisionWorkbench={openDecisionWorkbench}
                feedbackLoading={decisionActionLoading}
              />

              <section className={`mec-recommended-action ${closureAllowed ? "good" : blockingReasons.length ? "danger" : "neutral"}`}>
                <div>
                  <span>Recommended Action</span>
                  <h3>{recommendedActionTitle}</h3>
                  <p>{recommendedActionDetail}</p>
                </div>
                <div>
                  <StatusPill tone={closureAllowed ? "ok" : blockingReasons.length ? "danger" : "neutral"}>
                    {closureAllowed ? "Closure Ready" : blockingReasons.length ? "Closure Blocked" : titleCase(validationStatus)}
                  </StatusPill>
                  {requiredActions.length ? (
                    <ul>
                      {requiredActions.slice(0, 4).map((action, index) => <li key={`required-${index}`}>{action}</li>)}
                    </ul>
                  ) : (
                    <ul>
                      <li>Review task context.</li>
                      <li>Complete remediation steps.</li>
                      <li>Refresh evidence and validation.</li>
                    </ul>
                  )}
                </div>
              </section>

              <section className="mec-action-panel" aria-label="Operator notes and state">
                <div className="mec-action-panel-header">
                  <div>
                    <h3>Operator Notes & State</h3>
                    <p>Record operator notes or manage blocked state with confirmation.</p>
                  </div>
                </div>
                <div className="mec-note-row">
                  <textarea
                    value={operatorNote}
                    onChange={(event) => setOperatorNote(event.target.value)}
                    placeholder="Add operator note…"
                    aria-label="Operator note"
                    rows={2}
                  />
                  <button
                    type="button"
                    className="mec-primary-button"
                    onClick={addOperatorNote}
                    disabled={Boolean(actionLoading) || !clean(operatorNote)}
                  >
                    {actionLoading === "note" ? "Adding…" : "Add Note"}
                  </button>
                </div>

                {operatorNotes.length ? (
                  <div className="mec-compact-notes">
                    <span>Recent Notes</span>
                    {operatorNotes.map((event, index) => (
                      <div key={`${event.occurred_at || "note"}-${index}`}>
                        <strong>{event.message || event.metadata?.note || "Operator note recorded."}</strong>
                        <small>{event.actor_name || "Operator"} · {formatDateTime(event.occurred_at)}</small>
                      </div>
                    ))}
                  </div>
                ) : null}
                <div className="mec-compact-notes" aria-label="Blocking and exception state">
                  <span>Blocking / Exception State</span>
                  <div>
                    <strong>
                      {clean(selected?.status).toLowerCase() === "blocked"
                        ? "This work item is currently blocked."
                        : "This work item is not blocked."}
                    </strong>
                    <small>
                      {clean(selected?.status).toLowerCase() === "blocked"
                        ? "Use unblock only after the blocker is resolved or work can safely continue."
                        : "Use block only when evidence, workflow, validation, or an external dependency prevents safe progress."}
                    </small>
                  </div>
                  <div className="mec-action-buttons">
                    {clean(selected?.status).toLowerCase() === "blocked" ? (
                      blockConfirmation === "unblock" ? (
                        <>
                          <button
                            type="button"
                            className="mec-secondary-button"
                            onClick={() =>
                              runWorkItemAction(
                                "unblock",
                                "unblock",
                                "Operator unblocked this task from Mission Execution Center after confirming work can safely continue."
                              )
                            }
                            disabled={Boolean(actionLoading)}
                          >
                            {actionLoading === "unblock" ? "Unblocking…" : "Confirm Unblock Task"}
                          </button>
                          <button
                            type="button"
                            className="mec-link-button"
                            onClick={() => setBlockConfirmation("")}
                            disabled={Boolean(actionLoading)}
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          className="mec-secondary-button"
                          onClick={() => setBlockConfirmation("unblock")}
                          disabled={Boolean(actionLoading)}
                        >
                          Unblock Task
                        </button>
                      )
                    ) : blockConfirmation === "block" ? (
                      <>
                        <button
                          type="button"
                          className="mec-secondary-button mec-danger-button"
                          onClick={() =>
                            runWorkItemAction(
                              "block",
                              "block",
                              "Operator blocked this task from Mission Execution Center after confirmation."
                            )
                          }
                          disabled={Boolean(actionLoading)}
                        >
                          {actionLoading === "block" ? "Blocking…" : "Confirm Block Task"}
                        </button>
                        <button
                          type="button"
                          className="mec-link-button"
                          onClick={() => setBlockConfirmation("")}
                          disabled={Boolean(actionLoading)}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        className="mec-secondary-button mec-danger-button"
                        onClick={() => setBlockConfirmation("block")}
                        disabled={Boolean(actionLoading)}
                      >
                        Mark Blocked
                      </button>
                    )}
                  </div>
                </div>

              </section>
            </>
          ) : (
            <EmptyState title="No focused work selected" message="Choose a task from the operational work queue to view its actions, lifecycle, decisions, and operator state." />
          )}
        </section>
      </section>

      <section className="mec-intelligence-hub" aria-label="Execution intelligence">
        <div className="mec-intelligence-hub__header">
          <div>
            <span>Execution intelligence</span>
            <h2>Planning, optimization, and learning</h2>
            <p>Supporting advisory context remains available below the operational workbench without delaying active work.</p>
          </div>
        </div>
        <div className="mec-intelligence-hub__content">
          <OptimizationSummaryStrip
            context={workOptimizationContext}
            loading={workOptimizationLoading}
            error={workOptimizationErr}
            onRefresh={() => loadWorkOptimizationContext()}
          />

          <ExecutionPlanningSummaryStrip
            context={executionPlanningSummary}
            loading={executionPlanningSummaryLoading}
            error={executionPlanningSummaryErr}
            onRefresh={() => loadExecutionPlanningSummary()}
          />

          <LearningSummaryStrip
            context={learningSummaryContext}
            loading={learningSummaryLoading}
            error={learningSummaryErr}
            refreshState={learningSummaryRefreshState}
            onRefresh={() => loadLearningSummary(undefined, { manualRefresh: true })}
          />
        </div>
      </section>
      <TaskExecutionModal
        open={taskExecutionModal.open}
        task={modalTask}
        initialTab={taskExecutionModal.initialTab}
        onClose={closeTaskExecutionModal}
        remediationGuidanceContext={remediationGuidanceContext}
        remediationGuidanceLoading={remediationGuidanceLoading}
        remediationGuidanceError={remediationGuidanceErr}
        onRefreshRemediationGuidance={() => loadTaskRemediationGuidanceContext(remediationTaskIdForValidation(selected, selectedId))}
        optimizationContext={selectedOptimizationPanelContext}
        optimizationLoading={taskOptimizationLoading}
        optimizationError={taskOptimizationErr}
        onRefreshOptimization={() => loadTaskOptimizationContext(remediationTaskIdForValidation(selected, selectedId))}
        executionPlanContext={executionPlanContext}
        executionPlanLoading={executionPlanLoading}
        executionPlanError={executionPlanErr}
        onRefreshExecutionPlan={() => loadExecutionPlanContext(remediationTaskIdForValidation(selected, selectedId))}
        learningContext={taskLearningContext}
        learningLoading={taskLearningLoading}
        learningError={taskLearningErr}
        learningRefreshState={taskLearningRefreshState}
        onRefreshLearning={() => loadTaskLearningContext(remediationTaskIdForValidation(selected, selectedId), undefined, { manualRefresh: true })}
        intelligence={taskIntelligence}
        intelligenceLoading={taskIntelligenceLoading}
        intelligenceError={taskIntelligenceErr}
        intelligenceQuestion={taskIntelligenceQuestion}
        onRequestIntelligence={(question) =>
          loadTaskIntelligence(
            remediationTaskIdForValidation(selected, selectedId),
            question
          )
        }
        decision={selectedDecision}
        historyEvents={sortedTimeline}
        workflowEvents={workflowContext?.history?.length
          ? workflowContext.history
          : sortedTimeline.filter((event) =>
              clean(event?.event_type || event?.type || event?.category).toLowerCase().includes("workflow")
            )}
        workflowContext={workflowContext}
        workflowLoading={workflowContextLoading}
        workflowError={workflowContextErr}
        onRefreshWorkflow={() => loadTaskWorkflowContext(remediationTaskIdForValidation(selected, selectedId))}
        sopContext={sopContext}
        sopLoading={sopContextLoading}
        sopError={sopContextErr}
        onRefreshSOP={() => loadTaskSOPContext(remediationTaskIdForValidation(selected, selectedId))}
        validationEvents={validationModalEvents}
        evidenceRecords={evidenceModalRecords}
        evidenceContext={evidenceContext}
        evidenceLoading={evidenceContextLoading}
        evidenceError={evidenceContextErr}
        onRefreshEvidence={() => loadTaskEvidenceContext(remediationTaskIdForValidation(selected, selectedId))}
        onRunValidation={runValidationRerun}
        validationRunning={actionLoading === "validation_rerun"}
        onRefreshDecision={refreshDecisionSupport}
        onDecisionFeedback={recordDecisionSupportFeedback}
        decisionLoading={decisionSupportLoading || Boolean(decisionActionLoading)}
      />

    </div>
  );
}
